import * as React from 'react';

import { AppBar, Button, Checkbox, CircularProgress, Divider, FormControlLabel, FormGroup, Grid, IconButton, ListItem, ListItemAvatar, ListItemText, Menu, MenuItem, TextField, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { debounce } from '../utils';
import { CREATE, DataAPIContext, GET_ONE, GET_LIST } from '../api';
import { Close, NavigateBefore } from '@mui/icons-material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { ReactComponent as FilterListIconActive } from './icons/filter_list_icon_active.svg';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { ReactComponent as AppSupportIcon } from './icons/cell_phone.svg';
import { ReactComponent as WebChatIcon } from './icons/web_chat.svg';
import Avatar from 'react-avatar';
import ChatScreen from './ChatScreen';
import ConfirmationDialog from '../components/ConfirmationDialog';
import { ContextStore } from '../store/contextStore';
import DOMPurify from 'isomorphic-dompurify';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { ReactComponent as WaiverSignedIcon } from './icons/waiver_signed.svg';
import { Tag, chatColors, rowTourTagStyle, rowChatTagStyle, rowPaxStyle, headerTourTagStyle, headerPaxStyle } from './components/Tag';
import { checkoutLabel, staggeredCheckoutText } from './checkoutLabel';
import { tourDateRangeText } from './tourDateRange';
import { OverflowTooltip } from './components/OverflowTooltip';
import { ReactComponent as GroupChat } from './icons/group_chat.svg';
import { ReactComponent as GuidesAndStaff } from './icons/guides_and_staff.svg';
import { ReactComponent as MainOfficeIcon } from './icons/main_office.svg';
import { ReactComponent as GroupTourCoverPlaceholder } from './icons/group_tour_cover_placeholder.svg';
import { ReactComponent as IndividualAnnouncementsIcon } from './icons/individual_announcements.svg';
import { ReactComponent as MarkReadIcon } from './icons/mark_read.svg';
import { ReactComponent as Megaphone } from './icons/megaphone.svg';
import { ReactComponent as NotificationBellOff } from './icons/notification_bell_off_new.svg';
import { ReactComponent as NotificationBellOffGray } from './icons/notification_bell_off_gray_new.svg';
import { ReactComponent as NotificationBellOn } from './icons/notification_bell_on_new.svg';
import { ReactComponent as IssuesClosedIcon } from './icons/issues_closed.svg';
import { ReactComponent as IssuesAutoClosedIcon } from './icons/issues_auto_closed.svg';
import { ReactComponent as SystemAutoReplyIcon } from './icons/last_message_auto_replied.svg';
// The web-widget handoff badge is the chat header's stopped-chatbot dot, so red means the same thing in the list and the header.
import { ReactComponent as ChatbotHandoffIcon } from './icons/chatbot_settings_red_dot.svg';
import { ReactComponent as SystemAutoReplyAutoClosedIcon } from './icons/issues_auto_closed_auto_replied.svg';
import { ReactComponent as OnTripSupport } from './icons/on_trip_support.svg';
import PageMeta from '../components/PageMeta';
import { ReactComponent as PauseAutoCommunications } from './icons/pause_notifications.svg';
import { ReactComponent as PauseAutoCommunicationsGray } from './icons/pause_notifications_gray.svg';
import RecordList from './components/RecordList';
import { ReactComponent as UnpauseAutoCommunications } from './icons/unpause_notifications.svg';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import { simple_markdown_to_html, formatTourTagsTooltip, formatTagsTooltip, tagCategoryLabel } from './utils/chat_utils'

import axios from 'axios';
import config from '../config';
import { getTwilioClient, reqConfig } from './utils/chat_utils';
import useCloseOnPanelChange from './utils/useCloseOnPanelChange';
import { mobileChatPaneHeight } from '../layout/visualViewport';
import images from '../components/images';
import { makeStyles } from '@mui/styles';
//import distinctColors from 'distinct-colors'
import randomColor from 'randomcolor';
import { useSnackbar } from 'notistack';
import { cv } from '../themeVars';
import { aiAutoRepliesOn, andAiAutoReplies, aiStillSuggests } from './notificationPauseLabels';

//import MessagesList from "./MessagesList";
//import ConversationPreview from "./ConversationPreview";
//import ChatScreen from "./ChatScreen";
function useIsWidthUp(breakpoint) {
    const theme = useTheme();
    return useMediaQuery(theme.breakpoints.up(breakpoint));
}

// Lead-booker vs +1 (party member) indicators for a conversation row, plus a
// waiver-signed marker. Driven by the subject_* fields on the conversation
// serializer. Renders nothing for non-participant channels (Announcements, Main
// Office, etc.) where subject_is_lead_booker is null.
function BookingIndicators({ row, dense = false }) {
    if (!row || row.subject_is_lead_booker == null) {
        return null;
    }
    const isLead = row.subject_is_lead_booker;
    const waiverSigned = !!row.subject_waiver_signed;

    let label;
    let tooltip;
    let background;
    if (isLead) {
        const pax = row.subject_pax_count || 1;
        const others = Math.max(0, pax - 1);
        label = `PAX: ${pax}`;
        tooltip = `Lead Booker, +${others} passenger${others === 1 ? '' : 's'}`;
        background = chatColors.paxBg;
    } else {
        label = '+1';
        const leadName = row.subject_lead_booker_name;
        tooltip = leadName ? `${leadName} +1` : 'Party member (+1)';
        // Per Figma, the +1 chip uses the same green as the PAX chip (was tan #FDF3E1).
        background = chatColors.paxBg;
    }
    if (waiverSigned) {
        tooltip += ', Waiver signed';
    }

    return (
        <>
            <Tag title={tooltip} style={rowPaxStyle(row.unread_message_count > 0, dense, background)}>
                {label}
            </Tag>
            {waiverSigned ? (
                <Tag title="Waiver signed" style={{ display: 'inline-flex', verticalAlign: 'text-bottom' }}>
                    <WaiverSignedIcon width={19} height={19} />
                </Tag>
            ) : null}
        </>
    );
}

// A booking's own check-out (hotel stays), after the PAX chip. Rows whose booking
// ends at start + tour length get nothing: the header already shows the trip end.
function CheckoutChip({ row, dense = false }) {
    const checkout = checkoutLabel(row?.subject_checkout_ts, row?.tour_timezone);
    if (!checkout) {
        return null;
    }
    const style = {
        ...rowPaxStyle(row.unread_message_count > 0, dense, checkout.past ? chatColors.checkoutPastBg : chatColors.checkoutBg),
        color: checkout.past ? chatColors.checkoutPastText : chatColors.checkoutText,
    };
    return (
        <Tag title={checkout.tooltip} style={style}>
            {checkout.label}
        </Tag>
    );
}

const useStyles = props => makeStyles((theme) => ({
    root: {

        paddingTop: 0,
        margin: props.isEmbeddedInChatList ? 0 : theme.spacing(2),
        marginTop: 0,
        height: props.autoHeight ? null : props.heightOverride ? props.heightOverride : props.isEmbeddedInChatList ? null : "calc(86svh - 48px)",
        display: 'flex',
        [theme.breakpoints.down('sm')]: {
            //height: '92vh',
            //marginTop: 54,
            //margin: theme.spacing(2),
            paddingBottom: 56,
        },
        flex: 2,
        minWidth: props.isEmbeddedInChatList || props.show1pageScreen ? null : 720
        
    },
    gridRoot: { },
    recordList: {
        '&.MuiDataGrid-root': {
            borderRadius: 0,
            borderLeft: !props.show1pageScreen && !props.isEmbeddedInChatList ? `0px solid ${cv('border', 'rgb(224, 224, 224)')}` :  `1px solid ${cv('border', 'rgb(224, 224, 224)')}`,
            borderTop: `0px solid ${cv('border', 'rgb(224, 224, 224)')}`,
            minHeight: props.heightOverride || props.autoHeight ? null : "calc(42svh - 58px)"
            //height: props.isEmbeddedInChatList ? "calc(40svh - 57px) !important" : null
        },
        //marginTop: theme.spacing(2),
        '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none',
        },
        '&.MuiDataGrid-main': {
            outline: `1px solid ${cv('border', 'rgb(224, 224, 224)')}`,
        },
        [theme.breakpoints.down('sm')]: {
            overflowY: 'scroll',
            height: '100%',
        },
        '&.MuiDataGrid-root .MuiDataGrid-cell': {
            borderBottom: 0,
        },
        '&.MuiDataGrid-columnHeaders': {
            display: "none"
        },
    },
}));

// Mirrors _normalize_search_tokens in apiv2/views/edit/chat.py: a token >= 3 chars is
// searchable on its own; failing that, an all-short query whose raw text is
// >= _MIN_PHRASE_FALLBACK_LEN (5) is searched as ONE phrase rather than discarded — so
// "NO TI", typed toward a "NO TICKETS" chip, does reach the backend. Keep in step with
// the backend constants; the same pair lives in components/RecordList.js for the left panel.
const MIDDLE_PANEL_MIN_SEARCH_LEN = 3;
const MIDDLE_PANEL_MIN_PHRASE_SEARCH_LEN = 5;
const isMiddlePanelSearchable = (trimmed) => (
    trimmed.split(/\s+/).some((token) => token.length >= MIDDLE_PANEL_MIN_SEARCH_LEN)
    || trimmed.length >= MIDDLE_PANEL_MIN_PHRASE_SEARCH_LEN
);

const ConversationsList = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const history = useHistory();
    const location = useLocation();
    const routeParams = useParams();
    let routeParamsConversationUniqueName = React.useRef(routeParams.twilio_conversation_unique_name || null);
    const [lastUpdate] = React.useState(null);
    const [rowSummary, setRowSummary] = React.useState({
        numRows: -1,
        firstRowConversationUniqueName: '',
    });

    const [relatedLoading, setRelatedLoading] = React.useState(false);
    const [gridLoading, setGridLoading] = React.useState(false);
    // Report the same combined state the grid's own (possibly hidden) loading bar reflects
    React.useEffect(() => {
        props.onLoadingChange?.(gridLoading || relatedLoading);
    }, [gridLoading, relatedLoading]);
    const [query] = React.useState(null);
    const [chatChannelGroupTourId, setChatChannelGroupTourId] = React.useState(null);
    const [chatChannelTitleText, setChatChannelTitleText] = React.useState('');
    const [chatChannelCoverImage, setChatChannelCoverImage] = React.useState(null);
    const [chatChannelCategory, setChatChannelCategory] = React.useState('');
    // CP-8136: guide groups are staff-only spaces, so their one group conversation
    // is the INTERNAL thread -- that is the thread type a guest can never be a
    // participant on. Relabel it "Group Chat" there, since to those users it IS
    // the group chat; the real Group Chat thread type is guest-bearing by design.
    const [chatChannelIsStaffChatGroup, setChatChannelIsStaffChatGroup] = React.useState(null);
    const [chatChannelNotificationLevel, setChatChannelNotificationLevel] = React.useState(null);
    const [preAndPostTripNotificationsPaused, setPreAndPostTripNotificationsPaused] = React.useState(null);

    const [travelDate, setTravelDate] = React.useState("");
    const [lastDateOfTour, setLastDateOfTour] = React.useState("");
    const [tourStartDatetime, setTourStartDatetime] = React.useState("");
    const [tourEndDatetime, setTourEndDatetime] = React.useState("");
    const [tourTimezone, setTourTimezone] = React.useState("");
    const [checkoutSpread, setCheckoutSpread] = React.useState(null);

    const [tourTags, setTourTags] = React.useState([]);
    const [totalPassengerCount, setTotalPassengerCount] = React.useState(null);
    const [hasUnreadMessages, setHasUnreadMessages] = React.useState(false);
    const [listRefreshKey, setListRefreshKey] = React.useState(0);
    const [middlePanelRoleFilters, setMiddlePanelRoleFilters] = React.useState({ showGuests: true, showGuides: true, showStaff: true });
    const [roleFilterAnchorEl, setRoleFilterAnchorEl] = React.useState(null);
    useCloseOnPanelChange(props.selectedScreen, () => setRoleFilterAnchorEl(null));
    const classes = useStyles(props)();
    const isSmallScreen = !useIsWidthUp('lg') || props.isSmallScreen;
    const show1pageScreen = isSmallScreen || props.show3PanelChat === false;
    // True only for the desktop middle panel showing one channel, which owns a search
    // box of its own. Mobile and the list ChatList embeds keep the shared `searchText`.
    const ownsMiddlePanelSearch =
        !props.isEmbeddedInChatList && !show1pageScreen && !!props.selectedChannelID;

    const { settings, hasPerm } = React.useContext(ContextStore);

    const [APIState, setAPIState] = React.useState({
        onlyShowUnread: props.shouldShowUnreadOnly || false,
        onlyShowMissingInfo: props.onlyShowMissingInfo || false,
        extra_get_params_from_props: {...props.extra_get_params},
        extra_get_params: {...props.extra_get_params},
        resource: props.selectedChannelID ? `chat/${props.selectedChannelID}/conversations${props.shouldShowUnreadOnly ? '/unread_only' : ''}` : props.isEmbeddedInChatList ? `chat/all/conversations${props.shouldShowUnreadOnly ? '/unread_only' : ''}` : null,
        channelId: props.selectedChannelID
    });

    const refreshFromBackendFunc = React.useRef(null);
    // Use the shared ref if provided (for activity view), otherwise use the local ref
    const effectiveRefreshFromBackendFunc = props.onRefreshFromBackendFuncRef || refreshFromBackendFunc;
    const [searchText, setSearchText] = React.useState(props.conversationsListSearchText || location?.state?.ConversationsListSearchText || '');
    // The desktop middle panel's own search box (CP-8132). Deliberately NOT `searchText`:
    // that one doubles as mobile/embedded navigation state — it is written into router
    // state on row click and gates the post-click unread refresh — so driving it from
    // the middle panel leaked this panel's filtering into the left panel and into history.
    const [middlePanelSearchText, setMiddlePanelSearchText] = React.useState('');
    // What the box shows, updated on every keystroke; middlePanelSearchText is the
    // debounced value that actually reaches the API.
    const [middlePanelSearchInput, setMiddlePanelSearchInput] = React.useState('');
    // Unsearchable input (see isMiddlePanelSearchable) is stripped by the backend, which
    // then answers with the unfiltered list anyway, so don't send it at all. Too-short
    // input commits '' rather than nothing, so backspacing a committed search below the
    // minimum cannot strand the list filtered by a term the box no longer shows; while
    // typing forward the committed value is already '', making that a free no-op.
    const commitMiddlePanelSearchNow = React.useCallback((value) => {
        const trimmed = (value || '').trim();
        setMiddlePanelSearchText(isMiddlePanelSearchable(trimmed) ? trimmed : '');
    }, []);
    const commitMiddlePanelSearch = React.useMemo(
        () => debounce(commitMiddlePanelSearchNow, 400),
        [commitMiddlePanelSearchNow]
    );
    const clearMiddlePanelSearch = React.useCallback(() => {
        commitMiddlePanelSearch.cancel();
        setMiddlePanelSearchInput('');
        setMiddlePanelSearchText('');
    }, [commitMiddlePanelSearch]);
    // A commit still pending at unmount would otherwise fire into a dead component.
    React.useEffect(() => () => commitMiddlePanelSearch.cancel(), [commitMiddlePanelSearch]);
    // True while the box holds text that will not be sent; drives the "Keep typing to
    // search" hint so a too-short search visibly says why nothing is happening.
    // Derived, not state: the component re-renders on every keystroke anyway.
    const middlePanelSearchTooShort = (() => {
        const trimmed = middlePanelSearchInput.trim();
        return !!trimmed && !isMiddlePanelSearchable(trimmed);
    })();
    // Desktop header icons sit right-anchored in 2.15rem slots. Which icons exist
    // depends on the channel category — tours add pause + select-conversations,
    // support channels add the role filter — so the shared mark-read and bell icons
    // sit further left the more category-specific icons trail them. Counting slots
    // keeps every category flush against the panel's right edge.
    const trailingHeaderIconCount =
        chatChannelCategory === 'group_tour' ? 2
        : (chatChannelCategory === 'main_office' || chatChannelCategory === 'app_support') ? 1
        : 0;
    // Optical compensation, support channels only (measured off a screenshot,
    // 2026-08-30): the role-filter glyph tapers rightward and its explicitly sized
    // button centers its small glyph with ~2px more edge gap than the auto-sized
    // bell, so the bell and mark-read (the only users of this helper) slide 2px
    // toward it to make the three gaps read even.
    const headerIconOpticalNudgePx =
        (chatChannelCategory === 'main_office' || chatChannelCategory === 'app_support') ? 2 : 0;
    const desktopIconSlotRight = (slot) => {
        const rem = (0.5 + slot * 2.15).toFixed(2);
        return headerIconOpticalNudgePx ? `calc(${rem}rem - ${headerIconOpticalNudgePx}px)` : `${rem}rem`;
    };
    // Phone header icons overlay the DataGrid search toolbar (the AppBar is only
    // 61px; the icons sit at top 65px). Pack them against the right edge so the
    // bulk-select control is not sitting on the search field.
    const mobileIconSlotRight = (slot) => `${(0.15 + slot * 2.2).toFixed(2)}rem`;
    const [pageNum, setPageNum] = React.useState(location?.state?.ConversationsListPageNum || null);
    const [perPage, setPerPage] = React.useState(location?.state?.ConversationsListPerPage || (props.isRecentActivityView ? 15 : null));
    const [confirmationDialogAction, setConfirmationDialogAction] = React.useState(null);

    let selectedConversationUniqueNameRef = React.useRef(routeParams.twilio_conversation_unique_name || null);
    const [selectedConversationUniqueName, setSelectedConversationUniqueName] = React.useState(routeParams.twilio_conversation_unique_name || null)
    let selectedConversationAvatarColorRef = React.useRef(null);
    const [selectedConversationAvatarColor, setSelectedConversationAvatarColor] = React.useState(null)
    let selectedConversationRow = React.useRef(null);
    let childChatScreen = React.useRef(null);
    const [selectedRowBadgeKey, setSelectedRowBadgeKey] = React.useState(null);
    const [checkboxMode, setCheckboxMode] = React.useState(false);
    const [selectedConversations, setSelectedConversations] = React.useState(new Set());
    const eligibleConversationsRef = React.useRef(new Set()); // Track all eligible conversations on current page
    const [selectingAll, setSelectingAll] = React.useState(false); // Loading state for select all
    const { enqueueSnackbar } = useSnackbar();
    /*
    const paletteOptions = {
        count: 100,
        chromaMin: 48,
        chromaMax: 100,
        lightMin: 55,
        lightMax: 100,
        quality: 50
    };
    const palette = distinctColors(paletteOptions);
    */
    const palette = randomColor({ count: 17, seed: 4, format: 'rgba', alpha: 0.9 });
    let conversationAvatarColorDict = React.useRef({});

    React.useEffect(() => {
        const unlisten = history.listen((location) => {
            const url_parts = location.pathname?.split("/");
            console.log(`ConversationsList: Page changed to: ${location.pathname}`, routeParamsConversationUniqueName.current);

            // Update state immediately for responsive UI navigation
            if (url_parts && url_parts.length >= 5){
                if(routeParamsConversationUniqueName.current !== url_parts[4]){
                    routeParamsConversationUniqueName.current = url_parts[4];
                    selectedConversationUniqueNameRef.current = url_parts[4];
                    selectedConversationRow.current = null;
                    setPageNum(1);

                    console.log(`ConversationsList: Page changed; Conversation unique name changed to: ${selectedConversationUniqueNameRef.current}`);
                }
                else{
                    selectedConversationUniqueNameRef.current = url_parts[4]; //routeParams.twilio_conversation_unique_name || null; <- this is a valid parameter but sometimes seems to have a race condition when updating
                    selectedConversationRow.current = null;
                    setPageNum(1);
                }
            }
            else{
                //window.history.replaceState(null, "Chat", "/chat");
                routeParamsConversationUniqueName.current = null;
                selectedConversationUniqueNameRef.current = null;
                selectedConversationRow.current = null;
                setPageNum(1);
                if(!url_parts || url_parts.length < 3){
                    setChatChannelTitleText(props.selectedChannelName);
                }
            }
        });
        
        // Return cleanup function to unsubscribe
        return () => {
            unlisten();
        };
    }, [history])
    
    React.useEffect(() => {
        if(!props.selectedChannelID){
            return;
        }
        let client = null;
        (async () => {
            const chatChannelResource = 'chat';

            const res = await dataAPI(GET_ONE, chatChannelResource, { id: props.selectedChannelID,  include_inactive: true});
            //console.debug("ConversationsList: conversation details response: ", res)
            console.log('ConversationsList: Got channel details.', res.data);
            if (res.statusCode >= 400 && res.statusCode < 600) {
                console.warn('ConversationsList: Error getting channel by id', res.statusCode);
            } else {
                //console.debug("GETONE response:", res.data)
                let channel_title = isGuest && res.data.channel_category_type === 'main_office' ? 'New Bookings' : res.data.default_channel_name_without_date_or_tags;

                setChatChannelTitleText(channel_title);

                // link to trip per CP-3282. The id is kept in state rather than a
                // prebuilt anchor so the header can render the same link from the
                // selected row while this fetch is still in flight (or has failed).
                setChatChannelGroupTourId(res.data.group_tour_id || null);
                setChatChannelCoverImage(res.data.cover_image);
                setChatChannelCategory(res.data.channel_category_type);
                setChatChannelIsStaffChatGroup(res.data.is_staff_chat_group === true);
                setChatChannelNotificationLevel(res.data.is_muted_for_user === 'true' || res.data.is_muted_for_user === true ? 'muted' : 'default');
                setPreAndPostTripNotificationsPaused(res.data.all_notifications_paused == null ? null : res.data.all_notifications_paused === 'true' || res.data.all_notifications_paused === true ? true: false);
                setTravelDate(res.data.travel_date);
                setLastDateOfTour(res.data.last_date_of_tour);
                setTourStartDatetime(res.data.tour_start_datetime);
                setCheckoutSpread(res.data.checkout_spread || null);
                setTourEndDatetime(res.data.tour_end_datetime);
                setTourTimezone(res.data.group_tour_timezone);
                setTourTags(res.data.tour_tags);
                setTotalPassengerCount(res.data.total_passenger_count ?? null);
                setHasUnreadMessages(res.data.unread_message_count > 0);
                
                // routeParms is stale when the url changes to /chat, so only consider if the uniqe name is actually in the url
                if(window.location.pathname.split('/').length >= 5){
                    selectedConversationRow.current = routeParams.twilio_conversation_unique_name ?  selectedConversationRow.current : null;
                    selectedConversationUniqueNameRef.current = routeParams.twilio_conversation_unique_name || null;
                    setSelectedConversationUniqueName(routeParams.twilio_conversation_unique_name || null)
                    routeParamsConversationUniqueName.current = routeParams.twilio_conversation_unique_name || null;
                }
                else{
                    selectedConversationRow.current = null;
                    selectedConversationUniqueNameRef.current = null;
                    setSelectedConversationUniqueName(null)
                    routeParamsConversationUniqueName.current = null;
                }

            }
            console.log('ConversationsList: Getting chat client...');
            client = await getTwilioClient();
            console.log('ConversationsList: Client loaded!');

            client?.on('messageAdded', messageAddedHandler);
            client?.on('participantUpdated', participantUpdatedHandler);
        })();

        showAllOrUnread(APIState.onlyShowUnread);

        return () => {
            console.log('ConversationsList: Cleaning up messageAdded event listener');
            client?.removeListener('messageAdded', messageAddedHandler);
            client?.removeListener('participantUpdated', participantUpdatedHandler);
        };
    }, [dataAPI, props.selectedChannelID, props.shouldShowUnreadOnly]);

    // Turn off checkbox mode when the selected channel changes (including leaving a tour → no channel).
    const prevSelectedChannelIDRef = React.useRef(props.selectedChannelID);
    React.useEffect(() => {
        if (checkboxMode && prevSelectedChannelIDRef.current !== props.selectedChannelID) {
            setCheckboxMode(false);
            setSelectedConversations(new Set());
        }
        prevSelectedChannelIDRef.current = props.selectedChannelID;
    }, [props.selectedChannelID, checkboxMode]);

    // 1→3 panel while bulk messaging: reset (odd split with list vs compose).
    // 3→1 panel with bulk messaging: keep selections but show the list — selectedScreen was ChatScreen from
    // multi-panel entry, and 1-panel hides the list when selectedScreen !== ConversationsList.
    const prevShow3PanelChatRef = React.useRef(null);
    React.useEffect(() => {
        const prev = prevShow3PanelChatRef.current;
        const now3Panel = props.show3PanelChat === true;
        if (prev !== null && prev !== props.show3PanelChat) {
            if (now3Panel) {
                if (checkboxMode && props.selectedChannelID) {
                    props.setSelectedScreen?.("ConversationsList");
                }
                setCheckboxMode(false);
                setSelectedConversations(new Set());
            } else if (prev === true && checkboxMode && props.selectedChannelID) {
                props.setSelectedScreen?.("ConversationsList");
            }
        }
        prevShow3PanelChatRef.current = props.show3PanelChat;
    }, [props.show3PanelChat, props.selectedChannelID, props.setSelectedScreen]);
    
    // Clear eligible conversations ref when page changes or checkbox mode changes
    React.useEffect(() => {
        eligibleConversationsRef.current.clear();
    }, [pageNum, perPage, checkboxMode, APIState.resource]);

    const checkUnreadCount = async () => {
        const chatChannelResource = 'chat';

        const res = await dataAPI(GET_ONE, chatChannelResource, { id: props.selectedChannelID });
        //console.debug("ConversationsList: conversation details response: ", res)
        console.log('ConversationsList: checkUnreadCount: Got channel details.');
        if (res.statusCode >= 400 && res.statusCode < 600) {
            console.warn('ConversationsList: Error getting channel by id', res.statusCode);
        } else {
            setHasUnreadMessages(res.data.unread_message_count > 0);
        }
    };

    // handle channel changing via row clicks
    React.useEffect(() => {
        if ((parseInt(props.selectedChannelID) !== parseInt(APIState.channelId))){
            selectedConversationUniqueNameRef.current = null;
            // A search is scoped to the channel it was typed in; carrying it into the
            // next channel silently filters that one too.
            clearMiddlePanelSearch();
            setAPIState({
                onlyShowUnread: props.shouldShowUnreadOnly,
                onlyShowMissingInfo: false,
                extra_get_params_from_props: {...props.extra_get_params},
                extra_get_params: {...props.extra_get_params},
                channelId: props.selectedChannelID,
                resource: props.selectedChannelID ? `chat/${props.selectedChannelID}/conversations${props.shouldShowUnreadOnly ? '/unread_only' : ''}` : props.isEmbeddedInChatList ? `chat/all/conversations${props.shouldShowUnreadOnly ? '/unread_only' : ''}` : null,
            });
            setChatChannelTitleText(props.selectedChannelName);
            setChatChannelCoverImage(props.selectedChannelThumbnail);
            setChatChannelCategory(props.selectedChannelCategory);
            // Reset to null (not false) so the ?? fallback reads the new row until
            // the channel fetch resolves; a stale boolean from the previous channel
            // would win over the row and mislabel the internal thread (CP-8136).
            setChatChannelIsStaffChatGroup(null);
            setChatChannelGroupTourId(null);
            setChatChannelNotificationLevel(null);
            setPreAndPostTripNotificationsPaused(null);
            setTravelDate(null);
            setLastDateOfTour(null);
            setTourStartDatetime(null);
            setTourEndDatetime(null);
            setTourTimezone(null);
            setCheckoutSpread(null);
            setTourTags(null);
            setTotalPassengerCount(null);
            setHasUnreadMessages(false);
            setMiddlePanelRoleFilters({ showGuests: true, showGuides: true, showStaff: true });
        }
        else if(APIState.onlyShowUnread !== props.shouldShowUnreadOnly){
            setAPIState({
                ...APIState,
                onlyShowUnread: props.shouldShowUnreadOnly,
            });
        }
        else if(APIState.extra_get_params_from_props !== props.extra_get_params){
            setAPIState({
                ...APIState,
                onlyShowUnread: !props.shouldShowUnreadOnly,
                extra_get_params_from_props: {...props.extra_get_params},
                extra_get_params: {...props.extra_get_params}
            });
        }
    }, [props.selectedChannelID, props.shouldShowUnreadOnly, props.extra_get_params]);

    React.useEffect(() => {
        const roleFilter = {};
        if (!middlePanelRoleFilters.showGuests) roleFilter.hide_guest_conversations = true;
        if (!middlePanelRoleFilters.showGuides) roleFilter.hide_guide_conversations = true;
        if (!middlePanelRoleFilters.showStaff) roleFilter.hide_staff_conversations = true;
        if (Object.keys(roleFilter).length > 0) {
            setAPIState(prev => ({
                ...prev,
                extra_get_params: { ...prev.extra_get_params_from_props, ...roleFilter },
            }));
        } else {
            setAPIState(prev => ({
                ...prev,
                extra_get_params: { ...prev.extra_get_params_from_props },
            }));
        }
    }, [middlePanelRoleFilters]);

    // Update search text if applicable
    React.useEffect(() => {
        setSearchText(props.conversationsListSearchText)
    }, [props.conversationsListSearchText]);

    React.useEffect(() => {
        childChatScreen.current?.resize()
    }, [props.show3PanelChat])

    React.useEffect(() => {
        if (props.show1pageScreen !== true || props.selectedScreen !== "ConversationsList") { return; }
        // This is a hack to ensure that the record list doesn't stay stuck in a narrow (350 width) style
        const elements2 = document.querySelectorAll(["#ConversationsList .MuiDataGrid-root"])
        const timers = []
        elements2.forEach(element => {
            element.style.display = "none"
            timers.push(setTimeout(() => {
                element.style.display = null
            }, 5));
        })
        // Without this, an unmount or a re-run inside the 5ms window leaves the grid hidden.
        return () => {
            timers.forEach(clearTimeout)
            elements2.forEach(element => { element.style.display = null })
        }
    }, [props.show1pageScreen, props.selectedScreen])

    const messageAddedHandler = async (message) => {
        // Only process this if the message is in this conversation

        const chatChannelID = parseInt(message.conversation.uniqueName.split('::')[1]);

        console.log('ConversationsList: messageAddedHandler: message added!', message.conversation.uniqueName, "; Channel ID:", chatChannelID);

        if (chatChannelID === props.selectedChannelID) {
            setListRefreshKey((listRefreshKey) => listRefreshKey + 1);

            // add a small delay to give our backend time to process the new message
            /*setTimeout(
                () =>
                    (async () => {
                        await effectiveRefreshFromBackendFunc.current();
                    })(),
                500
            );*/
            // in case it takes extra long, add an additional refresh for insurance
            setTimeout(
                () =>
                    (async () => {
                        await effectiveRefreshFromBackendFunc.current();
                        const bootstrapUser = JSON.parse(window.localStorage.getItem('bootstrap'))?.user;
                        // Signed out (bootstrap wiped, e.g. by a logout in another tab): this tab's chat client is stale.
                        if (!bootstrapUser) return;
                        const tw_identity = bootstrapUser['twilio_identity'];
                        const msg_author = message.state.author;

                        if(msg_author !== tw_identity){
                            setHasUnreadMessages(true);
                        }
                    })(),
                3500
            );
        }
    };

    const participantUpdatedHandler = async (data) => {
        const bootstrapUser = JSON.parse(window.localStorage.getItem('bootstrap'))?.user;
        // Signed out (bootstrap wiped, e.g. by a logout in another tab): this tab's chat client is stale.
        if (!bootstrapUser) return;
        const tw_identity = bootstrapUser['twilio_identity'];
        const updated_participant_identity = await data.participant.identity;
        console.debug('ConversationsList: participantUpdatedHandler: participant updated!', data, tw_identity, updated_participant_identity);
        if (updated_participant_identity === tw_identity) {
            setListRefreshKey((listRefreshKey) => listRefreshKey + 1);
            //const unread_msg_count = data.participant.conversation.getUnreadMessagesCount();

            /*setTimeout(
                () =>
                    (async () => {
                        await effectiveRefreshFromBackendFunc.current();
                    })(),
                500
            );*/
            // in case it takes extra long, add an additional refresh for insurance
            setTimeout(
                () =>
                    (async () => {
                        await effectiveRefreshFromBackendFunc.current();
                        checkUnreadCount();
                    })(),
                3500
            );
        }
    };

    const removeUnreadBadge = React.useCallback(() => {
        if(selectedConversationRow.current?.unread_message_count > 0){
            props.updateSelectedRowBadgeCount?.(-selectedConversationRow.current.unread_message_count)
            selectedConversationRow.current.unread_message_count = 0;
            setSelectedRowBadgeKey(Math.random())
        }
    }, [props.isRecentActivityView, props.updateSelectedRowBadgeCount]);

    // If onRemoveUnreadBadgeRef is provided, register this instance's removeUnreadBadge function
    // This allows the parent (ChatList) to share it with other ConversationsList instances
    React.useEffect(() => {
        if (props.onRemoveUnreadBadgeRef) {
            props.onRemoveUnreadBadgeRef.current = removeUnreadBadge;
        }
        // Cleanup: clear the ref when component unmounts
        return () => {
            if (props.onRemoveUnreadBadgeRef) {
                props.onRemoveUnreadBadgeRef.current = null;
            }
        };
    }, [props.onRemoveUnreadBadgeRef, removeUnreadBadge]);

    const updateIssuesStateBadge = React.useCallback((newState) => {
        selectedConversationRow.current.issues_state = newState;
        setSelectedRowBadgeKey(Math.random())
    }, []);

    // Function to update unread badge count (used when marking conversation as unread)
    const updateUnreadBadgeCount = React.useCallback((newCount) => {
        if(selectedConversationRow.current){
            const oldCount = selectedConversationRow.current.unread_message_count || 0;
            selectedConversationRow.current.unread_message_count = newCount;
            // Update the badge count difference
            if(props.updateSelectedRowBadgeCount){
                props.updateSelectedRowBadgeCount(newCount - oldCount);
            }
            setSelectedRowBadgeKey(Math.random())
        }
    }, [props.updateSelectedRowBadgeCount]);

    // If onUpdateUnreadBadgeCountRef is provided, register this instance's updateUnreadBadgeCount function
    // This allows the parent (ChatList) to share it with other ConversationsList instances
    React.useEffect(() => {
        if (props.onUpdateUnreadBadgeCountRef) {
            props.onUpdateUnreadBadgeCountRef.current = updateUnreadBadgeCount;
        }
        // Cleanup: clear the ref when component unmounts
        return () => {
            if (props.onUpdateUnreadBadgeCountRef) {
                props.onUpdateUnreadBadgeCountRef.current = null;
            }
        };
    }, [props.onUpdateUnreadBadgeCountRef, updateUnreadBadgeCount]);

    // If onUpdateIssuesStateBadgeRef is provided, register this instance's updateIssuesStateBadge function
    // This allows the parent (ChatList) to share it with other ConversationsList instances
    React.useEffect(() => {
        if (props.onUpdateIssuesStateBadgeRef) {
            props.onUpdateIssuesStateBadgeRef.current = updateIssuesStateBadge;
        }
        // Cleanup: clear the ref when component unmounts
        return () => {
            if (props.onUpdateIssuesStateBadgeRef) {
                props.onUpdateIssuesStateBadgeRef.current = null;
            }
        };
    }, [props.onUpdateIssuesStateBadgeRef, updateIssuesStateBadge]);

    // If onRefreshFromBackendFuncRef is provided, also pass it to RecordList so it sets both refs
    // This allows the parent (ChatList) to share the refresh function with other ConversationsList instances

    const onRowClick = (params, ev, api) => {
        console.log('ConversationsList: row click', params, ev, api);

        // If in checkbox mode, toggle selection instead of navigating
        if (checkboxMode) {
            if (params.row.non_guest_participants_state === 'inactive') {
                return;
            }
            // Don't allow selection of Announcements, Internal Chat, or Group Chat
            if (params.row.is_announcements_thread || params.row.is_guides_and_staff_thread || params.row.is_group_chat_thread) {
                return;
            }
            const conversationId = params.row.twilio_conversation_unique_name;
            const newSelected = new Set(selectedConversations);
            if (newSelected.has(conversationId)) {
                newSelected.delete(conversationId);
            } else {
                newSelected.add(conversationId);
            }
            setSelectedConversations(newSelected);
            return;
        }

        setRelatedLoading(true);
        // The search deliberately survives opening a conversation: on desktop the list
        // stays visible beside the chat, and working through several results (say, every
        // conversation matching a surname) needs the filtered list to stay put. The box
        // and its clear button remain in view; channel switch still clears.
        const selectedChannelID = props.selectedChannelID ? props.selectedChannelID : params.row.twilio_conversation_unique_name.split("::")[1]
        
        //window.history.replaceState(null, "Chat", "/chat/" + props.selectedChannelID + "/conversations/" + params.row.twilio_conversation_unique_name + "/interactivechat");
        if (!show1pageScreen) {
            history.push("/chat/" + selectedChannelID + "/conversations/" + params.row.twilio_conversation_unique_name + "/interactivechat", { from: "ConversationsListSearch", })
        }
        else {
            history.push("/chat/" + selectedChannelID + "/conversations/" + params.row.twilio_conversation_unique_name + "/interactivechat", {
                from: 'ConversationsList',
                ChatListSearchText: location?.state?.ChatListSearchText,
                ChatListOnlyShowUnread: location?.state?.ChatListOnlyShowUnread,
                ChatListPageNum: location?.state?.ChatListPageNum,
                ChatListPerPage: location?.state?.ChatListPerPage,
                ConversationsListSearchText: searchText,
                ConversationsListOnlyShowUnread: APIState.onlyShowUnread,
                ConversationsListOnlyShowMissingInfo: APIState.onlyShowMissingInfo,
                ConversationsListPageNum: pageNum,
                ConversationsListPerPage: perPage,
            });
        }

        routeParamsConversationUniqueName.current = params.row.twilio_conversation_unique_name;
        selectedConversationRow.current = params.row;

        selectedConversationUniqueNameRef.current = params.row.twilio_conversation_unique_name;
        setSelectedConversationUniqueName(params.row.twilio_conversation_unique_name)
        selectedConversationAvatarColorRef.current = conversationAvatarColorDict.current[params.row.id]
        setSelectedConversationAvatarColor(conversationAvatarColorDict.current[params.row.id])
        //props.refreshChatListFromBackendFunc()
        props.setSelectedScreen("ChatScreen");
        childChatScreen.current?.scrollToBottom();
        setRelatedLoading(false);
        params.row.non_guest_participants_state = "active_synced"

        // if in search, make sure the unread counter gets cleared (it wasn't without this - for example in 
        // the participant updated callback - for some reason)
        if(searchText?.length > 0 && params.row.unread_message_count > 0){
            setTimeout(
                () =>
                    (async () => {
                        await effectiveRefreshFromBackendFunc.current();
                        checkUnreadCount();
                    })(),
                1500
            );
        }
    };

    const showAllOrUnread = (checked) => {
        
        //console.debug("Changing resource", checked)

        // if the toggle has changed, reset to the first page
        if (APIState.onlyShowUnread !== checked) {
            setPageNum(1);
            setRelatedLoading(true);
            routeParamsConversationUniqueName.current = null;
            selectedConversationRow.current = null;
            selectedConversationUniqueNameRef.current = null;
            setSelectedConversationUniqueName(null)
            selectedConversationAvatarColorRef.current = null
            setSelectedConversationAvatarColor(null)
            setRelatedLoading(false);
        }

        if (checked === true) {
            setAPIState({
                ...APIState,
                onlyShowUnread: true,
                channelId: props.selectedChannelID,
                resource: 'chat/' + props.selectedChannelID + '/conversations/unread_only',
            });
        }
        else {
            setAPIState({
                ...APIState,
                onlyShowUnread: false,
                channelId: props.selectedChannelID,
                resource: 'chat/' + props.selectedChannelID + '/conversations',
            });
        }
    };

    const showAllOrMissingInfo = (checked) => {
        //console.debug("Changing resource", checked)
        // if the toggle has changed, reset to the first page
        if (APIState.onlyShowMissingInfo !== checked) {
            setPageNum(1);
            setRelatedLoading(true);
            routeParamsConversationUniqueName.current = null;
            selectedConversationRow.current = null;
            selectedConversationUniqueNameRef.current = null;
            setSelectedConversationUniqueName(null)
            selectedConversationAvatarColorRef.current = null
            setSelectedConversationAvatarColor(null)
            setRelatedLoading(false);
        }
        if (checked === true) {
            setAPIState({
                ...APIState,
                onlyShowMissingInfo: true,
                extra_get_params: {...props.extra_get_params, only_show_missing_info: true},
                channelId: props.selectedChannelID,
                resource: APIState.resource,
            });
        }
        else {
            setAPIState({
                ...APIState,
                onlyShowMissingInfo: false,
                extra_get_params: {...props.extra_get_params},
                channelId: props.selectedChannelID,
                resource: APIState.resource,
            });
        }
    };

    const ConfirmationDialogText = {
        mark_all_as_read: { title: 'Please confirm', body: 'Are you sure you wish to mark all conversations in ' + (['main_office', 'app_support', 'web_widget'].includes(chatChannelCategory) ? chatChannelTitleText : 'this tour') + ' as “Read”?', okButtonText: 'Confirm', cancelButtonText: 'Cancel' },
        mute_conversations: { title: 'Please confirm', body: 'Are you sure you wish to mute all conversations in ' + (['main_office', 'app_support', 'web_widget'].includes(chatChannelCategory) ? chatChannelTitleText : 'this tour') + '?', okButtonText: 'Confirm', cancelButtonText: 'Cancel' },
        unmute_conversations: { title: 'Please confirm', body: 'Are you sure you wish to unmute all conversations in ' + (['main_office', 'app_support', 'web_widget'].includes(chatChannelCategory) ? chatChannelTitleText : 'this tour') + '?', okButtonText: 'Confirm', cancelButtonText: 'Cancel' },
        pause_guest_notifications: { title: 'Please confirm', body: `Are you sure you wish to pause all notifications${andAiAutoReplies()} for all guests for this tour?${aiStillSuggests()}`, okButtonText: 'Confirm', cancelButtonText: 'Cancel' },
        unpause_guest_notifications: { title: 'Please confirm', body: `Are you sure you wish to unpause all notifications${andAiAutoReplies()} for all guests for this tour?`, okButtonText: 'Confirm', cancelButtonText: 'Cancel' },
    
    };

    const markAllAsRead = async () => {
        // Sent through dataAPI so it carries the session token: the backend marks the signed-in
        // user's chats in this channel. user_id only keeps backends from before that change working
        // (they need it); the fixed backend ignores it.
        const data = {
            user_id: JSON.parse(window.localStorage.getItem('bootstrap'))?.user?.id,
            group_tour_chat_channel_id: props.selectedChannelID,
        };

        try {
            const res = await dataAPI(CREATE, 'chat/mark_all_conversations_as_read', { data });
            if (res.statusCode >= 400) {
                console.error('ConversationsList: chat/mark_all_conversations_as_read: Error setting all conversations as read!', res);
                return;
            }
            setTimeout(
                () =>
                    (async () => {
                        //call the left panel first (asynchronously) since it usually takes longer to update than the middle panel
                        props.refreshChatListFromBackendFunc();
                        await effectiveRefreshFromBackendFunc.current();
                        setHasUnreadMessages(false);
                    })(),
                300
            );
            setTimeout(
                () =>
                    (async () => {
                        //call the left panel first (asynchronously) since it usually takes longer to update than the middle panel
                        props.refreshChatListFromBackendFunc();
                        await effectiveRefreshFromBackendFunc.current();
                    })(),
                600
            );
        } catch (error) {
            console.error('ConversationsList: chat/mark_all_conversations_as_read: Error setting all conversations as read!', error);
        }
    };

    const handleMarkRead = (ev) => {
        setConfirmationDialogAction('mark_all_as_read');
    };

    const confirmationDialogCancel = () => {
        setConfirmationDialogAction(null);
    };
    const confirmationDialogConfirm = () => {
        console.log('confirm', confirmationDialogAction);
        switch (confirmationDialogAction) {
            case 'mark_all_as_read':
                markAllAsRead();
                break;
            case 'mute_conversations':
            case 'unmute_conversations':
                toggleNotificationLevel();
                break;
            case 'pause_guest_notifications':
            case 'unpause_guest_notifications':
                gtuNotificationsUpdate();
                break;
            default:
                break;
        }
        confirmationDialogCancel();
    };

    const handleToggleNotificationLevel = (ev) => {
        if (chatChannelNotificationLevel === 'default') {
            setConfirmationDialogAction('mute_conversations');
        } else {
            setConfirmationDialogAction('unmute_conversations');
        }
    };

    const toggleNotificationLevel = async () => {
        //e.stopPropagation();
        let newNotificationLevel;
        if (chatChannelNotificationLevel === 'default') {
            newNotificationLevel = 'muted';
        } else {
            newNotificationLevel = 'default';
        }

        setChatChannelNotificationLevel(newNotificationLevel);

        const tw_identity = JSON.parse(window.localStorage.getItem('bootstrap'))?.user['twilio_identity'];

        const params = { tour_channel_identifier: props.selectedChannelID, user_identity: tw_identity, notification_level: newNotificationLevel };

        setRelatedLoading(true)
        const response = await axios
            .post(`${config.apiUrl}/chat/set_conversation_notification_level/`, params, reqConfig())
            .then((response) => {
                (async () => {
                    await effectiveRefreshFromBackendFunc.current();
                    await props.refreshChatListFromBackendFunc?.();
                })()
                //console.debug(this.state.channel.notificationLevel, " -- ", this.state.notificationLevel )
            })
            .catch((error) => {
                if (error.response) {
                    console.error('ConversationsList: Error setting notification level!');
                    // sync back up with Twilio
                    //if (this.state.channel.notificationLevel !== this.state.notificationLevel){
                    //  this.setState({ notificationLevel: this.state.channel.notificationLevel })
                    //}
                }
            });
            setRelatedLoading(false)
    };

    const handleToggleGtuNotifications = (ev) => {
        if (preAndPostTripNotificationsPaused === true) {
            setConfirmationDialogAction('unpause_guest_notifications');
        } else {
            setConfirmationDialogAction('pause_guest_notifications');
        }
    };

    const gtuNotificationsUpdate = async () => {
        //e.stopPropagation();
        let shouldPause;
        if (preAndPostTripNotificationsPaused === true) {
            shouldPause = false;
        } else {
            shouldPause = true;
        }

        setPreAndPostTripNotificationsPaused(shouldPause);

        const userID = String(JSON.parse(window.localStorage.getItem('bootstrap'))?.user['id']);

        const params = { tour_channel_identifier: props.selectedChannelID, user_id: userID, action: shouldPause === true ? "PAUSE" : "UNPAUSE", };

        setRelatedLoading(true)
        const response = await axios
            .post(`${config.apiUrl}/chat/pause_group_tour_user_notifications/`, params, reqConfig())
            .then((response) => {
                (async () => {
                    await effectiveRefreshFromBackendFunc.current();
                    await props.refreshChatListFromBackendFunc?.();
                })()
                //console.debug(this.state.channel.notificationLevel, " -- ", this.state.notificationLevel )
            })
            .catch((error) => {
                if (error.response) {
                    enqueueSnackbar("ConversationsList: Error pausing/unpausing guest notifications", { variant: 'error' });
                    console.error("ConversationsList: Error pausing/unpausing guest notifications", error)
                }
            });
            setRelatedLoading(false)
    };
    
    const onSelectionModelChange = (ids) => {
        if (checkboxMode) {
            // Convert row IDs to conversation unique names
            const newSelected = new Set();
            // We need to get the conversation unique names from the row data
            // This will be handled by the checkbox clicks in renderCell
        }
    };
    const goBack = (e) => {
        e.stopPropagation();
        console.log("ConversationsList: goBack: " + location?.state?.ChatListSearchText)

        const elements = document.querySelectorAll(["#ConversationsList .MuiDataGrid-toolbarContainer .MuiInputBase-root .MuiButtonBase-root"])
        elements.forEach(element => {
            element.click()
            setSearchText("");
        })
        
        history.push('/chat', { 
            from: 'ConversationsList',
            ChatListSearchText: location?.state?.ChatListSearchText,
            ChatListOnlyShowUnread: location?.state?.ChatListOnlyShowUnread,
            ChatListPageNum: location?.state?.ChatListPageNum,
            ChatListPerPage: location?.state?.ChatListPerPage
        });
    };

    const listItemRenderer = (row, idx, rows, gridApi) => {
        return (
            <React.Fragment key={idx}>
                <ListItem
                    alignItems="flex-start"
                    className={classes.listItem}
                    onClick={(ev) => {
                        //console.debug('ConversationsList: click', row, idx);
                        onSelectionModelChange(row.id, { selectionModel: [row.id] });
                        // gridApi.onRowClick({ row, idx }, ev) }
                    }}
                >
                    <ListItemAvatar></ListItemAvatar>

                    <ListItemText primary={row.twilio_conversation_friendly_name || row.twilio_conversation_unique_name || `(conversations ${row.id})`} secondary={row.twilio_conversation_friendly_name} />
                </ListItem>
                {idx < rows.length - 1 ? <Divider /> : null}
            </React.Fragment>
        );
    };

    const setTotalAndFirstRow = (t, row) => {
        setRowSummary({ numRows: t, firstRowConversationUniqueName: row?.twilio_conversation_unique_name });
    };

    const cols = 12;
    let friendlyName = '';
    let channelSubjectID = null;
    let channelGroupID = null;
    let chatChannelID = null;

    const userID = String(JSON.parse(window.localStorage.getItem('bootstrap'))?.user['id']);
    const isAdmin = JSON.parse(window.localStorage.getItem('bootstrap'))?.user['is_admin'] || JSON.parse(window.localStorage.getItem('bootstrap'))?.user['is_rbac_user'];
    const isGuide = JSON.parse(window.localStorage.getItem('bootstrap'))?.user['is_guide'];
    // Matches both brand-wide `help@curatedplanet.com` and any per-licensee help-desk identity
    // (`help+<licensee.code>@curatedplanet.com`).
    const userIsAppSupport = /^help(\+[^@]+)?@curatedplanet\.com$/i.test(
        JSON.parse(window.localStorage.getItem('bootstrap'))?.user['email'] || ""
    );
    const isGuest = !(isAdmin || isGuide || userIsAppSupport);

    const imageHeight = 32;
    const imageWidth = 32;
    // The header's name, cover image, category and trip link are all state written
    // by the channel GET_ONE, and seeded from the selected row only in the
    // channel-change effect -- on a deep link / reload the row ref isn't populated
    // yet when that effect runs and never gets a second chance, so a failed or slow
    // fetch left them blank while the dates and tags below still rendered (those
    // fall back to the row on every render). Fall back the same way. The fetched
    // values still win: they overwrite this state as soon as they resolve.
    const headerCategory = chatChannelCategory || props.selectedChannelCategory;
    // The row carries the raw channel name, so the fallback has to repeat the
    // guest-facing Main Office rename that both the fetch above and the chat list
    // (channelDisplayName) apply -- otherwise a guest sees "Main Office" until the
    // fetch resolves, or indefinitely if it fails.
    const headerTitleText = chatChannelTitleText
        || (isGuest && headerCategory === 'main_office' ? 'New Bookings' : props.selectedChannelName);
    const headerGroupTourId = chatChannelGroupTourId || props.selectedChannelGroupTourId;
    // Same fetch-then-fall-back-to-the-row shape as headerCategory above, so a deep
    // link or a slow/failed channel fetch still labels the thread correctly.
    const isGuideGroupChannel = chatChannelIsStaffChatGroup ?? (props.selectedChannelIsStaffChatGroup === true);
    // Same gate as ChatScreen.canPostAnnouncements (CP-8136). The row's own
    // is_staff_chat_group decides (the all-conversations search embed mixes
    // channels, so the selected channel's flag says nothing about a given row);
    // the channel flag is only the fallback for a backend that doesn't send the
    // row field yet.
    const canPostGuideGroupAnnouncements = hasPerm('chat', 'post_guide_group_announcements');
    const headerCoverImage = chatChannelCoverImage || props.selectedChannelThumbnail;
    // Same shape guard the chat list uses on the row's cover_image (ChatList.js).
    const imgUrl = (headerCoverImage && headerCoverImage.p && headerCoverImage.s) ? images.url(settings.images.base, headerCoverImage, `cover_${imageWidth}x${imageHeight}`) : null;

    const dateSubtext = () => {
        // CP-8136: guide groups have no meaningful dates. Gated here rather than
        // on the selectedChannelDateRange prop, because this function also falls
        // back to the fetched tour datetimes and would refill the header anyway.
        if (isGuideGroupChannel) {
            return "";
        }
        if(props.selectedChannelDateRange){
            return props.selectedChannelDateRange;
        }
        if (isNaN(Date.parse(tourStartDatetime)) === true){
            return "";
        }
        // Same hotel-stay label the chat list row uses (see ChatList getDateRangeText).
        const staggered = staggeredCheckoutText(tourStartDatetime, checkoutSpread, tourTimezone);
        if (staggered) {
            return staggered;
        }
        return tourDateRangeText(tourStartDatetime, new Date(tourEndDatetime) - 1, tourTimezone) || "";
    };

    const tagsSubtext = () => {
        const tour_tags_temp = tourTags ? tourTags : props.selectedChannelTags;
        if (tour_tags_temp) {
            // Plain spans (no per-chip tooltip): the whole header subtext line carries a
            // single combined tooltip (see headerSubtextTooltip) so clipped chips stay
            // recoverable without nested tooltips.
            const tag_spans = tour_tags_temp.map((tag) => <span key={tag.id} style={headerTourTagStyle(tag.category === 'departure')}>{tag.data?.friendly_name || tag.name}</span>);
            return tag_spans;
        }
    };

    // Combined hover content for the truncating header subtext line: every tour tag
    // (with its category inside the chip) plus the total-PAX chip, so anything clipped
    // off the right edge stays recoverable on hover.
    const headerSubtextTooltip = () => {
        const tour_tags_temp = tourTags ? tourTags : props.selectedChannelTags;
        const hasPax = chatChannelCategory === 'group_tour' && totalPassengerCount != null;
        const hasContent = (tour_tags_temp && tour_tags_temp.length) || hasPax;
        if (!hasContent) return '';
        const chipWrap = { whiteSpace: 'normal' };
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4, maxWidth: 340 }}>
                {tour_tags_temp?.map((tag) => {
                    const label = tagCategoryLabel(tag);
                    const value = tag.data?.friendly_name || tag.name;
                    return (
                        <span key={`t${tag.id}`} style={{ ...headerTourTagStyle(tag.category === 'departure'), marginLeft: 0, ...chipWrap }}>
                            {label ? <><b>{label}:</b> {value}</> : value}
                        </span>
                    );
                })}
                {hasPax && (
                    <span style={{ ...headerPaxStyle(), marginLeft: 0, ...chipWrap }}><b>{`PAX: ${totalPassengerCount}`}</b> · Total passengers on this tour (lead bookers' party sizes, matching check-in)</span>
                )}
            </div>
        );
    };

    const handleSelectAllOrNone = async (e) => {
        e.stopPropagation();
        const eligible = Array.from(eligibleConversationsRef.current);
        const allSelected = eligible.length > 0 && eligible.every(id => selectedConversations.has(id));

        if (allSelected) {
            setSelectedConversations(new Set());
            return;
        }
        setSelectingAll(true);
        try {
            const resource = APIState.resource;
            if (!resource) {
                setSelectingAll(false);
                return;
            }
            const allConversations = [];
            let page = 1;
            const fetchPerPage = 100;
            let hasMore = true;
            while (hasMore) {
                const params = {
                    pagination: { page, perPage: fetchPerPage },
                    sort: { field: 'id', order: 'ASC' },
                    filter: {}
                };
                if (APIState.extra_get_params != null) {
                    for (var key in APIState.extra_get_params) {
                        if (!(key in params)) {
                            params[key] = JSON.stringify(APIState.extra_get_params[key]);
                        }
                    }
                }
                const res = await dataAPI(GET_LIST, resource, params);
                if (res && res.data && Array.isArray(res.data)) {
                    allConversations.push(...res.data);
                    const total = res.total || 0;
                    const fetched = allConversations.length;
                    if (res.data.length < fetchPerPage || fetched >= total || total < 0) {
                        hasMore = false;
                    } else {
                        page++;
                    }
                } else {
                    hasMore = false;
                }
            }
            const allEligible = allConversations
                .filter(conv =>
                    !conv.is_announcements_thread &&
                    !conv.is_guides_and_staff_thread &&
                    !conv.is_group_chat_thread &&
                    conv.non_guest_participants_state !== 'inactive' &&
                    conv.twilio_conversation_unique_name
                )
                .map(conv => conv.twilio_conversation_unique_name);
            const newSelected = new Set(selectedConversations);
            allEligible.forEach(id => newSelected.add(id));
            setSelectedConversations(newSelected);
        } catch (error) {
            console.error('ConversationsList: Error fetching all conversations for select all:', error);
            enqueueSnackbar('Error selecting all conversations', { variant: 'error' });
        } finally {
            setSelectingAll(false);
        }
    };

    const selectAllLabel = (() => {
        const eligible = Array.from(eligibleConversationsRef.current);
        const allSelected = eligible.length > 0 && eligible.every(id => selectedConversations.has(id));
        return allSelected ? 'Deselect All' : 'Select All';
    })();

    const mobileBulkSelect = show1pageScreen && !props.isEmbeddedInChatList && checkboxMode && chatChannelCategory === 'group_tour';

    return (
        <div id={"ConversationsList"} className={(!show1pageScreen || props.selectedScreen === "ConversationsList") && props.chatView !== 'recent_activity' ? classes.root : null} style={{
            // Embedded (Activity view) case: this root's height becomes the grid's height
            // (100%), and the CONVERSATIONS label above the grid pushes it down by 33.6px,
            // so for the grid's bottom border to land exactly on the left column's 86svh
            // anchor the root must be 86svh minus the collapsed channel grid (49px) minus
            // that label (33.6px) minus the measured SUPPORT section. The old
            // "+ 27px - 61px - 49px + (1px - .08vh)" version fell 0.6px + .08vh short.
            // (The search-embedded list gets null here — its grid heights come from
            // RecordList. Before this was accidental: it hit this branch with no
            // heightReductionOverride, producing "- 0" which made the calc() invalid
            // CSS that the browser dropped.)
            height: props.autoHeight ? "auto" : props.heightOverride ? props.heightOverride : !show1pageScreen && !props.isEmbeddedInChatList ? null : !props.isEmbeddedInChatList ? mobileChatPaneHeight() : props.isRecentActivityView ? "calc(86svh - 82.6px - " + (props.heightReductionOverride || "0px") + ")" : null,
            display: (show1pageScreen && props.selectedScreen === "ChatList") ? "none" : (props.chatView === 'recent_activity' || (props.isEmbeddedInChatList && props.isRecentActivityView && window.innerWidth < 500) ? 'flex' : null),
            margin: props.chatView === 'recent_activity' ? 0 : undefined,
            paddingTop: props.chatView === 'recent_activity' ? 0 : undefined,
            flex: props.chatView === 'recent_activity' ? 2 : undefined,
            ...(props.isEmbeddedInChatList && props.isRecentActivityView && window.innerWidth < 500 ? { flexDirection: 'column', minHeight: 0, overflow: 'hidden', paddingBottom: 0 } : {}),
        }}>
            <PageMeta title="Conversations" />
            {(!props.chatView || props.chatView !== 'recent_activity') &&
            <Grid container direction="row" spacing={2}
                style={{
                    minWidth: props.isEmbeddedInChatList ? null : 350,
                    marginLeft: props.isEmbeddedInChatList ? "0px" : show1pageScreen ? "2px" : null,
                    marginTop: props.isEmbeddedInChatList ? "0px" : show1pageScreen ? "2px" : null,
                    display: (show1pageScreen && props.selectedScreen !== "ConversationsList") ? "none" : null,
                    ...(props.isEmbeddedInChatList && props.isRecentActivityView && window.innerWidth < 500 ? { flex: 1, minHeight: 0, height: '100%' } : {}),
                }} >
                {(true || APIState.channelId) ?
                    <Grid item xs={cols} style={{ height: '100%', paddingLeft: 0, paddingTop: props.isEmbeddedInChatList ? 0 : null, marginTop: props.isEmbeddedInChatList ? 0 : null, ...((show1pageScreen && !props.isEmbeddedInChatList) || (props.isEmbeddedInChatList && props.isRecentActivityView && window.innerWidth < 500) ? { display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' } : {}) }}>
                        {/* Embedded lists keep their section label even with 0 rows: the left
                            column's heights (RecordList's search formulas and this component's
                            Activity-view root height above) are tuned to sum to 86svh WITH this
                            label, so hiding it floated the panel's bottom border ~34px above the
                            neighbouring panels whenever the list was empty. Only the Activity
                            view's SUPPORT sub-list still hides its label when empty — it sizes
                            to content (autoHeight) and a bare "SUPPORT" heading over nothing is
                            clutter; alignment survives because the list below measures this
                            section's real height. */}
                        {props.isEmbeddedInChatList && (rowSummary.numRows !== 0 || !props.isRecentActivitySupportView) ? <Typography style={styles.dateStatusLabel}>
                            {props.title_override_text || "CONVERSATIONS"}
                        </Typography> : ""}
                        {props.isEmbeddedInChatList ? null : <div style={{ height: 4 }} aria-hidden />}
                        <AppBar position="relative" elevation={0} style={styles.header(show1pageScreen, props)}>
                            {!props.isEmbeddedInChatList && !show1pageScreen && props.selectedScreen !== "ChatList" && (
                                <IconButton
                                    onClick={goBack}
                                    style={{
                                        position: "absolute",
                                        right: 4,
                                        top: 4,
                                        zIndex: 10,
                                        color: cv('text-secondary', '#666'),
                                        padding: "2px"
                                    }}
                                    size="small"
                                >
                                    <Close sx={{ fontSize: "14px" }} />
                                </IconButton>
                            )}
                            {//show1pageScreen && !props.isEmbeddedInChatList && <FormControlLabel style={{ position: 'absolute', right: -6, top: 4, zIndex: 10 }} control={<Switch size="small" color="primary" disabled={APIState.resource == null} onChange={(e) => showAllOrUnread(e.target.checked)} checked={APIState.onlyShowUnread} />} label={<Typography style={{ fontSize: ".75rem" }}>{"Unread"}</Typography>} />
                            }
                            {/*!!chatChannelTitleText && <FormControlLabel style={{ position: 'absolute', right: show1pageScreen && !props.isEmbeddedInChatList? -6 : 0, top: !show1pageScreen ? '76px' : '32px', zIndex: 10 }} control={<Switch size="small" color="primary" disabled={APIState.resource == null} onChange={(e) => showAllOrMissingInfo(e.target.checked)} checked={APIState.onlyShowMissingInfo} />} label={<Typography style={{fontSize: ".75rem"}}>{"Missing data"}</Typography>} />*/}
                            <div style={{ display: 'flex', flexDirection: 'row', zIndex: 9 }}>
                                {(show1pageScreen && props.selectedScreen === "ConversationsList") ? <NavigateBefore style={{ cursor: "pointer", color: cv('accent', '#007AFF'), marginTop: "11px", fontSize: "28px" }} onClick={goBack} /> : ""}
                                {headerCategory === 'main_office' ? (
                                    <MainOfficeIcon style={styles.topicImg} />
                                ) : headerCategory === 'app_support' ? (
                                    <AppSupportIcon style={styles.topicImg} />
                                ) : headerCategory === 'web_widget' ? (
                                    <WebChatIcon style={styles.topicImg} />
                                ) : imgUrl !== null && imgUrl !== 'null' ? (
                                    <img src={`${imgUrl}`} alt="" style={{ height: imageHeight, width: imageWidth, objectFit: 'cover', borderRadius: '50%', marginRight: 12, marginTop: 5 }} />
                                ) : headerCategory ? (
                                    // Tours with no cover image show the same placeholder the chat
                                    // list uses for their row, rather than an empty gap. Gated on
                                    // the category so it only appears once we know the channel has
                                    // no image -- on a reload nothing is known yet, and drawing it
                                    // unconditionally flashed a placeholder before the real icon.
                                    <GroupTourCoverPlaceholder style={{ height: imageHeight, width: imageWidth, marginRight: 12, marginTop: 5, flexShrink: 0 }} />
                                ) : (
                                    ''
                                )}
                                <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                    {/* Same reason: no TOUR/SUPPORT placeholder until the category
                                        is known, so the header appears in one step instead of
                                        flashing a guessed label first. */}
                                    {!headerTitleText && !!headerCategory && <Typography style={{ color: cv('text-muted', '#999999'), fontWeight: "500", fontFamily: "Roboto", fontSize: ".875rem", lineHeight: "1.03rem" }}>
                                        {['main_office', 'app_support', 'web_widget'].includes(headerCategory) ? "SUPPORT" : "TOUR"}
                                    </Typography>}
                                    <OverflowTooltip title={headerTitleText}><Typography style={{ display: "inline-block", fontWeight: 600, maxWidth: !show1pageScreen ? 'calc(100% - 40px)' : 'calc(100% - 20px)', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                        {headerGroupTourId ? (
                                            <a style={{ color: cv('text', 'black') }} href={'/trip/' + headerGroupTourId} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                                                {headerTitleText}
                                            </a>
                                        ) : headerTitleText}
                                    </Typography></OverflowTooltip>
                                    <Tooltip
                                        title={headerSubtextTooltip()}
                                        arrow
                                        placement="top-start"
                                        slotProps={{
                                            tooltip: { sx: { bgcolor: cv('surface', '#fff'), color: cv('text', '#202020'), border: `1px solid ${cv('border', 'rgba(0,0,0,0.12)')}`, boxShadow: '0 2px 10px rgba(0,0,0,0.18)', p: 0.75, maxWidth: 360 } },
                                            arrow: { sx: { color: cv('surface', '#fff'), '&::before': { border: `1px solid ${cv('border', 'rgba(0,0,0,0.12)')}` } } },
                                        }}
                                    >
                                    <Typography style={styles.conversationsListHeaderSubtext(show1pageScreen, isSmallScreen)}>
                                        {['main_office', 'app_support', 'web_widget'].includes(headerCategory) && "SUPPORT CHANNEL"}
                                        {/* CP-8136: fills the slot the suppressed dates left empty and
                                            names the channel kind -- the left panel's GUIDE GROUPS
                                            section header isn't visible here on deep links / 2-panel. */}
                                        {isGuideGroupChannel && "STAFF-ONLY GUIDE GROUP"}
                                        {dateSubtext()}
                                        {tagsSubtext()}
                                        {chatChannelCategory === 'group_tour' && totalPassengerCount != null && (
                                            <span style={headerPaxStyle()}>
                                                {`PAX: ${totalPassengerCount}`}
                                            </span>
                                        )}
                                    </Typography>
                                    </Tooltip>
                                </div>
                                {!mobileBulkSelect && (show1pageScreen || props.selectedScreen !== "ChatList") && chatChannelCategory === 'group_tour' ? preAndPostTripNotificationsPaused ?
                                    <Tooltip title={`Unpause all notifications${andAiAutoReplies()} for all guests on this tour`} arrow placement="top"><IconButton style={{ cursor: 'pointer', height: '2.5rem', position: 'absolute', top: '65px', left: null, right: !show1pageScreen ? '2.65rem' : mobileIconSlotRight(0) }} onClick={(e) => chatChannelNotificationLevel != null ? handleToggleGtuNotifications(e) : e.preventDefault()}>
                                        <UnpauseAutoCommunications style={{ fontSize: '2.5em', marginBottom: "1px" }} />
                                    </IconButton></Tooltip> :
                                    <Tooltip title={`Pause all notifications${andAiAutoReplies()} for all guests on this tour${aiAutoRepliesOn() ? ' (the AI still suggests replies)' : ''}`} arrow placement="top"><IconButton style={{ cursor: 'pointer', height: '2.5rem', position: 'absolute', top: '65px', left: null, right: !show1pageScreen ? '2.65rem' : mobileIconSlotRight(0) }} onClick={(e) => chatChannelNotificationLevel != null ? handleToggleGtuNotifications(e) : e.preventDefault()}>
                                        <PauseAutoCommunications style={{ fontSize: '2.5em', marginBottom: "1px" }} />
                                    </IconButton></Tooltip>

                                    :
                                    ""
                                }
                                {/* Like the header icon and TOUR label, the bell and mark-read wait
                                    for the category: their slot depends on which category-specific
                                    icons follow them, so drawing them earlier means a visible jump. */}
                                {!mobileBulkSelect && (show1pageScreen || props.selectedScreen !== "ChatList") && !!chatChannelCategory &&

                                    <Tooltip title={(chatChannelNotificationLevel === 'default' ? 'Mute' : 'Unmute') + ' notifications for all conversations in ' + headerTitleText} arrow placement="top"><IconButton style={{ cursor: 'pointer', height: '2.5rem', position: 'absolute', top: '65px', left: null, right: !show1pageScreen ? desktopIconSlotRight(trailingHeaderIconCount) : mobileIconSlotRight(1) }} onClick={(e) => chatChannelNotificationLevel != null ? handleToggleNotificationLevel(e) : e.preventDefault()}>
                                        {chatChannelNotificationLevel == null ?
                                            <NotificationBellOffGray style={{ fontSize: '2.5em' }} />
                                        :
                                        chatChannelNotificationLevel === 'default' ? (
                                            <NotificationBellOff style={{ fontSize: '2.5em' }} />
                                        ) : (
                                            <NotificationBellOn style={{ fontSize: '2.5em' }} />
                                        )}
                                    </IconButton></Tooltip>
                                }
                                {!mobileBulkSelect && (show1pageScreen || props.selectedScreen !== "ChatList") && !!chatChannelCategory &&
                                    <Tooltip title={'Mark All Conversations in ' + headerTitleText + ' as Read'} arrow placement="top"><IconButton style={{ zIndex: 10, height: '2.5rem', cursor: hasUnreadMessages ? 'pointer' : 'default', position: 'absolute', top: '65px', left: null, right: !show1pageScreen ? desktopIconSlotRight(trailingHeaderIconCount + 1) : mobileIconSlotRight(2) }} onClick={(e) => hasUnreadMessages ? handleMarkRead(e) : e.preventDefault()}>
                                        <MarkReadIcon fill={hasUnreadMessages ? "#007AFF" : "rgba(0, 0, 0, 0.26)"} style={{ fontSize: '1.75em' }} variant="contained" size="small" />
                                    </IconButton></Tooltip>
                                }
                                {/* Search within this channel (CP-8132). Absolutely positioned into
                                    the header's existing 109px band, on the same 65px row as the
                                    icons, so the middle panel stays flush with the panels either
                                    side -- a DataGrid toolbar would add a row and push it taller. */}
                                {ownsMiddlePanelSearch && !checkboxMode && (
                                    <TextField
                                        variant="standard"
                                        value={middlePanelSearchInput}
                                        // Names the fields it searches, like the chat list's box,
                                        // rather than the channel -- the channel name is already the
                                        // header directly above, and interpolating it produced
                                        // "Search Waiver", which reads as a noun, not a scope.
                                        placeholder="Search by name, booking, email"
                                        inputProps={{
                                            'aria-label': headerTitleText
                                                ? `Search conversations in ${headerTitleText}`
                                                : "Search conversations in this channel",
                                        }}
                                        onChange={(e) => {
                                            setMiddlePanelSearchInput(e.target.value);
                                            commitMiddlePanelSearch(e.target.value);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Escape') { clearMiddlePanelSearch(); }
                                            // Enter skips the debounce and searches now.
                                            if (e.key === 'Enter') {
                                                commitMiddlePanelSearch.cancel();
                                                commitMiddlePanelSearchNow(middlePanelSearchInput);
                                            }
                                        }}
                                        InputProps={{
                                            // Bare small icons and an always-mounted, visibility-toggled
                                            // clear button, exactly as GridToolbarQuickFilter renders
                                            // them -- also keeps the input width from jumping on the
                                            // first keystroke.
                                            startAdornment: <SearchIcon fontSize="small" />,
                                            endAdornment: (
                                                <IconButton
                                                    size="small"
                                                    aria-label="Clear search"
                                                    style={{ visibility: middlePanelSearchInput ? 'visible' : 'hidden' }}
                                                    onClick={clearMiddlePanelSearch}
                                                >
                                                    <Close fontSize="small" />
                                                </IconButton>
                                            ),
                                            style: { marginRight: '5px' },
                                        }}
                                        helperText={middlePanelSearchTooShort ? "Keep typing to search" : null}
                                        sx={{
                                            // Same arrangement as the chat list's search box: field on
                                            // the left, icon row to its right. Every style token below
                                            // (white rounded block on the grey band, hairline divider
                                            // underline, default type size, width formula) mirrors
                                            // GridToolbarQuickFilter plus the toolbar sx in RecordList,
                                            // so the two panels read as one control.
                                            position: 'absolute',
                                            top: '68px',
                                            left: '12px',
                                            // Ends 0.9rem short of the mark-read icon, however many
                                            // slots this category's icon row occupies.
                                            width: `min(calc(100% - ${(trailingHeaderIconCount * 2.15 + 6.05).toFixed(2)}rem), min(800px, 72%))`,
                                            backgroundColor: cv('surface', 'white'),
                                            borderRadius: '5px',
                                            paddingLeft: '8px',
                                            paddingBottom: '2px',
                                            '& .MuiInput-underline:before': {
                                                borderBottom: '1px solid',
                                                borderBottomColor: 'divider',
                                            },
                                            '& input': { marginLeft: 0.5 },
                                            // The too-short hint floats inside the right end of the box
                                            // (clear of the X button), styled identically to the left
                                            // panel's, so it never grows the field past the header band.
                                            '& .MuiFormHelperText-root': {
                                                position: 'absolute',
                                                right: '42px',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                margin: 0,
                                                pointerEvents: 'none',
                                                whiteSpace: 'nowrap',
                                                color: cv('text-muted', '#9e9e9e'),
                                            },
                                        }}
                                    />
                                )}
                                {(show1pageScreen || props.selectedScreen !== "ChatList") && (chatChannelCategory === 'main_office' || chatChannelCategory === 'app_support') && (
                                    <>
                                        <Tooltip title="Filter conversations by role" arrow placement="top">
                                        <IconButton
                                            style={{
                                                zIndex: 10,
                                                height: '2.5rem',
                                                width: '2.5rem',
                                                cursor: 'pointer',
                                                position: 'absolute',
                                                top: '65px',
                                                left: null,
                                                // Rightmost slot: support channels have no pause or
                                                // select-conversations button after this one.
                                                right: !show1pageScreen ? '0.5rem' : '1.0rem',
                                            }}
                                            onClick={e => setRoleFilterAnchorEl(e.currentTarget)}
                                        >
                                            {(!middlePanelRoleFilters.showGuests || !middlePanelRoleFilters.showGuides || !middlePanelRoleFilters.showStaff)
                                                ? <FilterListIconActive style={{ color: cv('accent', '#007AFF'), fontSize: "1em" }} />
                                                : <FilterListIcon style={{ color: cv('accent', '#007AFF'), fontSize: "1em" }} />
                                            }
                                        </IconButton>
                                        </Tooltip>
                                        <Menu
                                            anchorEl={roleFilterAnchorEl}
                                            open={Boolean(roleFilterAnchorEl)}
                                            onClose={() => setRoleFilterAnchorEl(null)}
                                            elevation={0}
                                            // CP-6445: see StyledMenu wrappers — avoid an orphaned body scroll lock.
                                            disableScrollLock
                                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                            PaperProps={{
                                                sx: {
                                                    borderRadius: '6px',
                                                    marginTop: 1,
                                                    minWidth: 200,
                                                    maxWidth: 300,
                                                    backgroundColor: cv('surface-alt', '#FCFCFE'),
                                                    color: cv('text', 'rgb(55, 65, 81)'),
                                                    boxShadow: 'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
                                                    '& .MuiMenu-list': { padding: '4px 0' },
                                                    '& .MuiMenuItem-root': {
                                                        paddingTop: '0px',
                                                        paddingBottom: '0px',
                                                        maxHeight: '24px',
                                                        '& .MuiSvgIcon-root': { fontSize: 18 },
                                                    },
                                                    '& .MuiFormControlLabel-root': {
                                                        width: '120%',
                                                        marginRight: 0,
                                                        paddingRight: '16px',
                                                        '& .MuiTypography-root': {
                                                            fontSize: '.9em',
                                                            fontWeight: 500,
                                                        },
                                                    },
                                                }
                                            }}
                                        >
                                            <div style={{ textAlign: "left", fontWeight: 600, marginLeft: "16px", marginBottom: "1px", marginTop: "8px" }}>Show Conversations</div>
                                            <FormGroup>
                                                <MenuItem style={{ paddingRight: 0 }}>
                                                    <FormControlLabel
                                                        control={<Checkbox size="small" checked={middlePanelRoleFilters.showGuests} />}
                                                        label="Guests"
                                                        onClick={(ev) => {
                                                            if (ev.target.checked !== undefined) {
                                                                setMiddlePanelRoleFilters({ ...middlePanelRoleFilters, showGuests: ev.target.checked });
                                                            }
                                                        }}
                                                    />
                                                </MenuItem>
                                                <MenuItem style={{ paddingRight: 0 }}>
                                                    <FormControlLabel
                                                        control={<Checkbox size="small" checked={middlePanelRoleFilters.showGuides} />}
                                                        label="Guides"
                                                        onClick={(ev) => {
                                                            if (ev.target.checked !== undefined) {
                                                                setMiddlePanelRoleFilters({ ...middlePanelRoleFilters, showGuides: ev.target.checked });
                                                            }
                                                        }}
                                                    />
                                                </MenuItem>
                                                <MenuItem style={{ paddingRight: 0 }}>
                                                    <FormControlLabel
                                                        control={<Checkbox size="small" checked={middlePanelRoleFilters.showStaff} />}
                                                        label="Staff"
                                                        onClick={(ev) => {
                                                            if (ev.target.checked !== undefined) {
                                                                setMiddlePanelRoleFilters({ ...middlePanelRoleFilters, showStaff: ev.target.checked });
                                                            }
                                                        }}
                                                    />
                                                </MenuItem>
                                            </FormGroup>
                                            <Divider sx={{ my: 0.5 }} />
                                            <div style={{ textAlign: "left", fontWeight: 400, marginLeft: "16px", marginTop: "8px", marginBottom: "8px" }}>
                                                <span
                                                    style={{ cursor: "pointer" }}
                                                    onClick={() => setMiddlePanelRoleFilters({ showGuests: true, showGuides: true, showStaff: true })}
                                                >
                                                    <u>Reset</u>
                                                </span>
                                            </div>
                                        </Menu>
                                    </>
                                )}
                                {(show1pageScreen || props.selectedScreen !== "ChatList") && chatChannelCategory === 'group_tour' &&
                                    <Tooltip title={checkboxMode ? 'Exit selection mode' : 'Select conversations to message'} arrow placement="top"><IconButton
                                        style={{
                                            zIndex: 10,
                                            height: '2.5rem',
                                            width: '2.5rem',
                                            cursor: 'pointer',
                                            position: 'absolute',
                                            top: mobileBulkSelect ? '8px' : '67px',
                                            left: null,
                                            right: !show1pageScreen ? '0.5rem' : (mobileBulkSelect ? '4px' : mobileIconSlotRight(3)),
                                            backgroundColor: checkboxMode ? cv('accent-tint-strong', '#D0E7FF') : null
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            const newCheckboxMode = !checkboxMode;
                                            setCheckboxMode(newCheckboxMode);
                                            if (newCheckboxMode) {
                                                // The search box is hidden in selection mode; drop the
                                                // term with it so the list is not left filtered by a
                                                // control the user can no longer see or clear.
                                                clearMiddlePanelSearch();
                                                // Clear the selected conversation when entering checkbox mode
                                                selectedConversationRow.current = null;
                                                selectedConversationUniqueNameRef.current = null;
                                                routeParamsConversationUniqueName.current = null;
                                                setSelectedConversationUniqueName(null);
                                                selectedConversationAvatarColorRef.current = null;
                                                setSelectedConversationAvatarColor(null);
                                                // Navigate away from the conversation URL if we're in one
                                                const currentPath = window.location.pathname;
                                                if (currentPath.includes('/conversations/') && currentPath.includes('/interactivechat')) {
                                                    const pathParts = currentPath.split('/');
                                                    const channelIdIndex = pathParts.indexOf('chat') + 1;
                                                    if (channelIdIndex > 0 && pathParts[channelIdIndex]) {
                                                        history.push(`/chat/${pathParts[channelIdIndex]}/conversations`, {
                                                            from: 'ConversationsList',
                                                            ChatListSearchText: location?.state?.ChatListSearchText,
                                                            ChatListOnlyShowUnread: location?.state?.ChatListOnlyShowUnread,
                                                            ChatListPageNum: location?.state?.ChatListPageNum,
                                                            ChatListPerPage: location?.state?.ChatListPerPage,
                                                            ConversationsListSearchText: searchText,
                                                            ConversationsListOnlyShowUnread: APIState.onlyShowUnread,
                                                            ConversationsListOnlyShowMissingInfo: APIState.onlyShowMissingInfo,
                                                            ConversationsListPageNum: pageNum,
                                                            ConversationsListPerPage: perPage,
                                                        });
                                                    }
                                                }
                                                // Activate ChatScreen when entering checkbox mode (multi-panel: chat beside list; one-panel: list stays visible and ChatScreen mounts below)
                                                if (!show1pageScreen) {
                                                    props.setSelectedScreen("ChatScreen");
                                                }
                                            } else {
                                                setSelectedConversations(new Set());
                                            }
                                        }}
                                    >
                                        <IndividualAnnouncementsIcon style={{ }} />
                                    </IconButton></Tooltip>
                                }
                                {checkboxMode && !mobileBulkSelect && (show1pageScreen || props.selectedScreen !== "ChatList") && chatChannelCategory === 'group_tour' &&
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: '70px',
                                            left: '12px',
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            gap: '8px',
                                            zIndex: 10,
                                        }}
                                    >
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            style={{
                                                height: '2rem',
                                                fontSize: '0.75rem',
                                                textTransform: 'none',
                                                padding: '4px 12px',
                                                minWidth: 'auto',
                                            }}
                                            onClick={handleSelectAllOrNone}
                                            disabled={selectingAll}
                                        >
                                            {selectingAll ? (
                                                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <CircularProgress size={14} style={{ color: cv('accent', '#007AFF') }} />
                                                    Loading...
                                                </span>
                                            ) : selectAllLabel}
                                        </Button>
                                    </div>
                                }
                            </div>

                            {confirmationDialogAction ? (
                                <ConfirmationDialog
                                    classes={{ paper: classes.paper }}
                                    keepMounted
                                    onCancel={confirmationDialogCancel}
                                    onConfirm={confirmationDialogConfirm}
                                    title={ConfirmationDialogText[confirmationDialogAction].title}
                                    okButtonText={ConfirmationDialogText[confirmationDialogAction].okButtonText}
                                    cancelButtonText={ConfirmationDialogText[confirmationDialogAction].cancelButtonText}
                                    open={!!confirmationDialogAction}
                                >
                                    {ConfirmationDialogText[confirmationDialogAction].body}
                                </ConfirmationDialog>
                            ) : null}
                        </AppBar>
                        {mobileBulkSelect && (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    padding: '8px 12px',
                                    backgroundColor: cv('surface-muted', '#F5F5F5'),
                                    borderBottom: `1px solid ${cv('border', 'rgb(224, 224, 224)')}`,
                                    flexShrink: 0,
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    size="small"
                                    style={{
                                        height: '2rem',
                                        fontSize: '0.75rem',
                                        textTransform: 'none',
                                        padding: '4px 12px',
                                        minWidth: 'auto',
                                        backgroundColor: cv('surface', '#fff'),
                                    }}
                                    onClick={handleSelectAllOrNone}
                                    disabled={selectingAll}
                                >
                                    {selectingAll ? (
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <CircularProgress size={14} style={{ color: cv('accent', '#007AFF') }} />
                                            Loading...
                                        </span>
                                    ) : selectAllLabel}
                                </Button>
                                <Typography style={{ flex: 1, fontSize: '0.8125rem', color: cv('text-secondary', '#555'), minWidth: 0 }}>
                                    {selectedConversations.size === 0
                                        ? 'Select guests to message'
                                        : `${selectedConversations.size} selected`}
                                </Typography>
                                <Tooltip title={selectedConversations.size === 0 ? 'Select at least one conversation' : 'Open composer and send to selected guests'} arrow placement="top">
                                    <span>
                                        <Button
                                            variant="contained"
                                            disableElevation
                                            size="small"
                                            disabled={selectedConversations.size === 0}
                                            style={{
                                                height: '2rem',
                                                fontSize: '0.75rem',
                                                textTransform: 'none',
                                                padding: '4px 14px',
                                                minWidth: 'auto',
                                                backgroundColor: selectedConversations.size === 0 ? undefined : cv('accent-fill', '#007AFF'),
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                props.setSelectedScreen("ChatScreen");
                                            }}
                                        >
                                            Compose
                                        </Button>
                                    </span>
                                </Tooltip>
                            </div>
                        )}
                        {/* Single-page: flex child so the grid footer stays inside the
                            visible pane. Desktop / embedded: display:contents keeps this
                            wrapper out of layout so the tuned height formulas and the
                            DataGrid height:100% chain behave exactly as before. */}
                        <div style={(show1pageScreen && !props.isEmbeddedInChatList) || (props.isEmbeddedInChatList && props.isRecentActivityView && window.innerWidth < 500) ? { flex: 1, minHeight: 0, overflow: 'hidden' } : { display: 'contents' }}>
                        <RecordList
                            refreshFromBackendFunc={effectiveRefreshFromBackendFunc}
                            listRefreshKey={listRefreshKey}
                            headerHeight={0}
                            className={classes.recordList}
                            resource={APIState.resource}
                            disableSelectionOnClick={false}
                            onSelectionModelChange={onSelectionModelChange}
                            // Always the shared state: the grid's own quick filter, the only
                            // thing that calls this, is mobile/embedded-only. The desktop box
                            // writes middlePanelSearchText itself.
                            onSearchTextChange={setSearchText}
                            initialSearchText={ownsMiddlePanelSearch ? middlePanelSearchText : searchText}
                            searchText={ownsMiddlePanelSearch ? middlePanelSearchText : searchText}
                            deps={lastUpdate}
                            onRowClick={onRowClick}
                            relatedLoading={relatedLoading}
                            query={query}
                            show1pageScreen={show1pageScreen}
                            isEmbeddedInChatList={props.isEmbeddedInChatList}
                            rowHeight={80}
                            setTotalAndFirstRow={setTotalAndFirstRow}
                            //listItemRenderer={listItemRenderer}
                            checkboxSelection={false}
                            pageNum={pageNum}
                            perPage={perPage}
                            setPageNum={setPageNum}
                            setPerPage={setPerPage}
                            getSelectedRow={selectedConversationRow.current?.id}
                            // The desktop middle panel renders its own box in the header band
                            // above (CP-8132); the grid's built-in toolbar stays mobile-only so
                            // it cannot add a row and unbalance the three panel headers.
                            hideQuickFilter={props.isEmbeddedInChatList || !props.show1pageScreen || checkboxMode}
                            hideToolbar={mobileBulkSelect}
                            searchPlaceholder={show1pageScreen ? "Search by name, booking" : "Search by name, booking, email"}
                            hideFooter={props.hideFooter}
                            hideBottomBorder={props.hideBottomBorder}
                            hideLoadingIndicator={props.hideLoadingIndicator}
                            onLoadingChange={setGridLoading}
                            //hideFooter={false && props.isEmbeddedInChatList}
                            hideHeader={props.isEmbeddedInChatList}
                            heightOverride={props.heightOverride}
                            autoHeight={props.autoHeight}
                            chatListRowTotal={props.chatListRowTotal}
                            extra_get_params={APIState.extra_get_params}
                            rowsPerPageOptions={searchText?.length && !props.isRecentActivityView && (!show1pageScreen || props.isEmbeddedInChatList) && [5]}
                            searchResultsPerPage={props.isRecentActivityView ? perPage || 25 : null}
                            isRecentActivityView={props.isRecentActivityView}
                            parent={"ConversationsList"}
                            columns={[
                                {
                                    field: 'twilio_conversation_friendly_name',
                                    headerName: 'Conversation Name',
                                    sortable: true,
                                    flex: 5,
                                    valueGetter: (p) => p.row.twilio_conversation_friendly_name,
                                    renderCell: (p) => {
                                        //console.debug('ConversationsList: renderCell', p.id);
                                        //console.debug('ConversationsList: is_announcements_thread', p.row.is_announcements_thread)
                                        const isSelected = checkboxMode && selectedConversations.has(p.row.twilio_conversation_unique_name);
                                        
                                        // Track eligible conversations (exclude Announcements, Internal Chat, Group Chat, and inactive)
                                        const isEligible = !p.row.is_announcements_thread && !p.row.is_guides_and_staff_thread && !p.row.is_group_chat_thread && p.row.non_guest_participants_state !== 'inactive';
                                        if (isEligible && p.row.twilio_conversation_unique_name) {
                                            eligibleConversationsRef.current.add(p.row.twilio_conversation_unique_name);
                                        }
                                        
                                        return (
                                            <span
                                                key={props.show1pageScreen}
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'row',
                                                    paddingTop: 4,
                                                    paddingBottom: !props.isEmbeddedInChatList ? 4 : 0,
                                                    minWidth: 0,
                                                    maxWidth: '100%',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                {checkboxMode && (
                                                    <Tooltip
                                                        title={isEligible ? '' : (
                                                            p.row.non_guest_participants_state === 'inactive' ? 'Inactive conversations can’t be messaged' :
                                                            p.row.is_announcements_thread ? 'Announcements can’t be bulk-messaged' :
                                                            p.row.is_guides_and_staff_thread ? 'Internal chat can’t be bulk-messaged' :
                                                            p.row.is_group_chat_thread ? 'Group chat can’t be bulk-messaged' :
                                                            'This conversation can’t be bulk-messaged'
                                                        )}
                                                        arrow
                                                        placement="top"
                                                    >
                                                        <span>
                                                            <Checkbox
                                                                checked={isSelected}
                                                                disabled={!isEligible}
                                                                style={{ padding: '4px', marginRight: '8px' }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (!isEligible) return;
                                                                    const conversationId = p.row.twilio_conversation_unique_name;
                                                                    const newSelected = new Set(selectedConversations);
                                                                    if (newSelected.has(conversationId)) {
                                                                        newSelected.delete(conversationId);
                                                                    } else {
                                                                        newSelected.add(conversationId);
                                                                    }
                                                                    setSelectedConversations(newSelected);
                                                                }}
                                                            />
                                                        </span>
                                                    </Tooltip>
                                                )}
                                                {(() => {
                                                    friendlyName = p.row.twilio_conversation_friendly_name.split('::')[1];
                                                    channelSubjectID = p.row.twilio_conversation_unique_name.split('::')[2];
                                                    channelGroupID = p.row.twilio_conversation_unique_name.split('::')[0];
                                                    chatChannelID = p.row.twilio_conversation_unique_name.split('::')[1];
                                                    conversationAvatarColorDict.current[p.row.id] = palette[p.row.id % palette.length];

                                                    // handle redirects (for example, from clicking a notification)
                                                    if (
                                                        routeParamsConversationUniqueName.current !== null &&
                                                        selectedConversationRow.current?.twilio_conversation_unique_name !==
                                                            routeParamsConversationUniqueName.current
                                                    ) {
                                                        if (
                                                            p.row.twilio_conversation_unique_name ===
                                                            routeParamsConversationUniqueName.current
                                                        ) {
                                                            routeParamsConversationUniqueName.current =
                                                                p.row.twilio_conversation_unique_name;
                                                            selectedConversationRow.current = p.row;
                                                            selectedConversationUniqueNameRef.current =
                                                                p.row.twilio_conversation_unique_name;
                                                            setSelectedConversationUniqueName(p.row.twilio_conversation_unique_name);
                                                            selectedConversationAvatarColorRef.current =
                                                                palette[p.row.id % palette.length];
                                                            setSelectedConversationAvatarColor(
                                                                palette[p.row.id % palette.length]
                                                            );
                                                        }
                                                    } else if (
                                                        selectedConversationRow.current?.twilio_conversation_unique_name.split(
                                                            '::'
                                                        )[1] !== chatChannelID
                                                    ) {
                                                        if (
                                                            p.row.twilio_conversation_unique_name ===
                                                            selectedConversationUniqueNameRef.current
                                                        ) {
                                                            //window.history.replaceState(null, "Chat", "/chat/" + chatChannelID + "/conversations/" + p.row.twilio_conversation_unique_name + "/interactivechat");
                                                            history.push(
                                                                '/chat/' +
                                                                    chatChannelID +
                                                                    '/conversations/' +
                                                                    p.row.twilio_conversation_unique_name +
                                                                    '/interactivechat'
                                                            );
                                                            routeParamsConversationUniqueName.current =
                                                                p.row.twilio_conversation_unique_name;
                                                            selectedConversationRow.current = p.row;
                                                            selectedConversationUniqueNameRef.current =
                                                                p.row.twilio_conversation_unique_name;
                                                            setSelectedConversationUniqueName(p.row.twilio_conversation_unique_name);
                                                            selectedConversationAvatarColorRef.current =
                                                                palette[p.row.id % palette.length];
                                                            setSelectedConversationAvatarColor(
                                                                palette[p.row.id % palette.length]
                                                            );
                                                        }
                                                    }
                                                    // make sure the ref gets updated on re-renders
                                                    if (
                                                        selectedConversationRow.current?.id === p.row.id &&
                                                        selectedConversationRow.current !== p.row
                                                    ) {
                                                        selectedConversationRow.current = p.row;
                                                    }

                                                    if (p.row.is_announcements_thread) {
                                                        // to convert from 📣 ANNOUNCEMENTS 📣
                                                        friendlyName = 'Announcements';
                                                        return <Megaphone style={styles.coverImg} />;
                                                    } else if (p.row.is_guides_and_staff_thread) {
                                                        // In a guide group every participant is staff, so this
                                                        // thread IS the group chat (CP-8136).
                                                        friendlyName = isGuideGroupChannel ? 'Group Chat' : 'INTERNAL: Guides/Staff';
                                                        return isGuideGroupChannel
                                                            ? <GroupChat style={styles.coverImg} />
                                                            : <GuidesAndStaff style={styles.coverImg} />;
                                                    } else if (p.row.is_group_chat_thread) {
                                                        friendlyName = 'Group Chat';
                                                        return <GroupChat style={styles.coverImg} />;
                                                    } else if (channelSubjectID === userID) {
                                                        if (channelGroupID === 'app_support') {
                                                            friendlyName = 'App Support';
                                                            return <AppSupportIcon style={styles.coverImg} />;
                                                        } else if (channelGroupID === 'main_office') {
                                                            friendlyName = isGuest ? 'New Bookings' : 'Main Office';
                                                            return <MainOfficeIcon style={styles.coverImg} />;
                                                        } else if (isGuide) {
                                                            // A guide's own tour 1:1 is their line to the office --
                                                            // titled "Manager", matching the mobile apps (CP-8286).
                                                            friendlyName = 'Manager';
                                                            return <OnTripSupport style={styles.coverImg} />;
                                                        } else {
                                                            friendlyName = 'Tour Guides/Support';
                                                            return <OnTripSupport style={styles.coverImg} />;
                                                        }
                                                    } else {
                                                        const sz = 43;

                                                        const avatarImg = p.row.subject_user_profile_img
                                                            ? p.row.subject_user_profile_img['p'] !== '' ||
                                                              p.row.subject_user_profile_img['s'] !== ''
                                                                ? images.url(
                                                                      settings.images.base,
                                                                      p.row.subject_user_profile_img,
                                                                      `cover_${sz}x${sz}`
                                                                  )
                                                                : null
                                                            : null;
                                                        return (
                                                            <Avatar
                                                                name={friendlyName}
                                                                style={styles.coverImg}
                                                                size={sz}
                                                                round={true}
                                                                color={palette[p.row.id % palette.length]}
                                                                src={avatarImg}
                                                                maxInitials={1}
                                                                textSizeRatio={2}
                                                            />
                                                        );
                                                    }
                                                })()}
                                                <div style={{ flex: 1, minWidth: 0, overflow: 'hidden'}}>{!props.isRecentActivitySupportView && (props.isEmbeddedInChatList || props.isRecentActivityView) ? (
                                                    <Typography
                                                        style={styles.conversationNameRowInLeftPanel(
                                                            p.row.unread_message_count > 0,
                                                            show1pageScreen
                                                        )}
                                                    >
                                                        <span style={{ overflowWrap: 'anywhere' }}>{p.row.channel_name}</span>
                                                        {p.row.tour_tags?.map((tag) => (
                                                            <Tag key={tag.id} title={formatTourTagsTooltip(p.row.tour_tags)} style={rowTourTagStyle(p.row.unread_message_count > 0, tag.category === 'departure')}>
                                                                {tag.data?.friendly_name || tag.name}
                                                            </Tag>
                                                        ))}
                                                        {p.row.visible_only_chat_tags_set?.map((tag) => (
                                                            <Tag key={tag.id} title={formatTagsTooltip(p.row.visible_only_chat_tags_set)} style={rowChatTagStyle(p.row.unread_message_count > 0, true)}>
                                                                {tag.data?.friendly_name || tag.name}
                                                            </Tag>
                                                        ))}
                                                        {/* PAX / +1 indicator goes last, after the other tags */}
                                                        <BookingIndicators row={p.row} dense />
                                                        <CheckoutChip row={p.row} dense />
                                                    </Typography>) : null}
                                                    <Typography
                                                        style={styles.conversationNameRow(
                                                            p.row.unread_message_count > 0,
                                                            props
                                                        )}
                                                    >
                                                        {/* Hack to make sure the unread badge doesn't get truncated off in portrait */}
                                                        <OverflowTooltip title={friendlyName}>
                                                        <span
                                                            style={styles.conversationNameText(
                                                                p.row.unread_message_count > 0,
                                                                show1pageScreen,
                                                                props
                                                            )}
                                                        >
                                                            {friendlyName}

                                                        </span>
                                                        </OverflowTooltip>
                                                        {/* The PAX indicator is rendered at the end of the tag row
                                                            (above for embedded/recent-activity panels, below for the
                                                            standard panel). Only the SUPPORT section -- which has no
                                                            tag row -- still shows it inline on the name line. */}
                                                        {props.isRecentActivitySupportView ? <BookingIndicators row={p.row} /> : null}
                                                    </Typography>
                                                    {!props.isEmbeddedInChatList && !props.isRecentActivityView && (p.row.visible_only_chat_tags_set?.length || p.row.subject_is_lead_booker != null || p.row.subject_checkout_ts) ? (
                                                        <div
                                                            style={{
                                                                // Dedicated wrapping row for the tags so they flow onto
                                                                // new lines (flush-left) instead of being truncated with
                                                                // an ellipsis on the single-line conversation-name row.
                                                                display: 'flex',
                                                                flexWrap: 'wrap',
                                                                alignItems: 'center',
                                                                columnGap: 4,
                                                                rowGap: 3,
                                                                marginLeft: '15px',
                                                                marginTop: 1,
                                                                marginBottom: 2,
                                                            }}
                                                        >
                                                            {p.row.visible_only_chat_tags_set?.map((tag) => (
                                                                <Tag key={tag.id} title={formatTagsTooltip(p.row.visible_only_chat_tags_set)} style={rowChatTagStyle(p.row.unread_message_count > 0, false)}>
                                                                    {tag.data?.friendly_name || tag.name}
                                                                </Tag>
                                                            ))}
                                                            {/* PAX / +1 indicator goes last, after the other tags */}
                                                            <BookingIndicators row={p.row} />
                                                            <CheckoutChip row={p.row} />
                                                        </div>
                                                    ) : null}
                                                    {(
                                                    <>
                                                            <Typography
                                                                style={styles.previewText(
                                                                    p.row.unread_message_count > 0,
                                                                    show1pageScreen,
                                                                    props
                                                                )}
                                                            >
                                                        {DOMPurify.sanitize(
                                                            (() => {
                                                                        let previewText =
                                                                            p.row.latest_message_in_conversation;
                                                                if (previewText != null) {
                                                                            // allow_links unconditionally: this preview renders as inert
                                                                            // plain text (React child), so restoring an anchor here just
                                                                            // lets the tag-strip below remove it cleanly, leaving its
                                                                            // inner text — no clickable link is created, so the trusted-
                                                                            // sender gating ChatItem needs doesn't apply. Without this,
                                                                            // a pasted <a href=...> shows as raw escaped tag text (the
                                                                            // strip regex only removes real tags). Matches ChatList's
                                                                            // de-facto behavior; see Ben-approved rule in chat_utils.js.
                                                                            previewText =
                                                                                simple_markdown_to_html(previewText, { allow_links: true });
                                                                }

                                                                if (p.row.is_announcements_thread) {
                                                                    const rowIsGuideGroup = p.row.is_staff_chat_group ?? isGuideGroupChannel;
                                                                    const canPostThisAnnouncement = !isGuest && (!rowIsGuideGroup || canPostGuideGroupAnnouncements);
                                                                                return (
                                                                                    previewText ||
                                                                                    (canPostThisAnnouncement
                                                                                        ? 'Post announcements here.'
                                                                                        : 'See important announcements here.')
                                                                                );
                                                                }
                                                                if (p.row.is_group_chat_thread) {
                                                                            return (
                                                                                previewText ||
                                                                                'Chat with your entire tour group here.'
                                                                            );
                                                                } else if (p.row.is_guides_and_staff_thread) {
                                                                    if (isAdmin === true) {
                                                                                return (
                                                                                    previewText ||
                                                                                    "Chat internally with this tour's guide(s)."
                                                                                );
                                                                    } else if (isGuide === true) {
                                                                                return (
                                                                                    previewText ||
                                                                                    'Chat internally with other guides/staff.'
                                                                                );
                                                                            } else {
                                                                                return (
                                                                                    previewText ||
                                                                                    "Chat internally with this tour's guide(s)."
                                                                                );
                                                                    }
                                                                } else if (channelSubjectID === userID) {
                                                                    if (channelGroupID === 'main_office') {
                                                                        if (isGuest === true) {
                                                                                    return (
                                                                                        previewText ||
                                                                                        'Contact our booking team here.'
                                                                                    );
                                                                        } else {
                                                                                    return (
                                                                                        previewText ||
                                                                                        'Contact our main office here.'
                                                                                    );
                                                                        }
                                                                    } else if (channelGroupID === 'app_support') {
                                                                                return (
                                                                                    previewText ||
                                                                                    'Chat with our app support team.'
                                                                                );
                                                                    } else if (isGuide) {
                                                                                return (
                                                                                    previewText ||
                                                                                    'Chat with your manager here.'
                                                                                );
                                                                    } else {
                                                                                return (
                                                                                    previewText ||
                                                                                    "Contact your tour's support team here."
                                                                                );
                                                                    }
                                                                } else {
                                                                            return (
                                                                                previewText ||
                                                                                'Chat with ' + friendlyName + ' here.'
                                                                            );
                                                                }
                                                            })()
                                                        )
                                                            .replace(/<\/?[^>]+(>|$)/g, '')
                                                            // Decode after tag-stripping: DOMPurify (and escape_html)
                                                            // turn a raw "<" into &lt;, which would otherwise show
                                                            // literally here since this is rendered as a React child.
                                                            // &amp; must come last so "&lt;" typed as text survives.
                                                            .replace(/&lt;/g, '<')
                                                            .replace(/&gt;/g, '>')
                                                            .replace(/&amp;/g, '&')}
                                                            </Typography>
                                                        </>
                                                    )}
                                                </div>
                                            </span>
                                        );
                                    },
                                },
                                {
                                    field: 'most_recent_msg_datetime',
                                    sortable: false,
                                    width: (isSmallScreen ? 122 : 122),
                                    minWidth: (isSmallScreen ? 122 : 122),
                                    maxWidth: (isSmallScreen ? 122 : 122),
                                    renderCell: (p) => {
                                        return ( 
                                            <div style={{
                                                marginLeft: 'auto',
                                                marginRight: 8,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'flex-end',
                                                minWidth: 0,
                                            }}>
                                                {true ? <Typography style={styles.timestampText}>
                                                    {(() => {
                                                        let latest_timestamp = null;
                                                        if (p.row.most_recent_msg_datetime) {
                                                            latest_timestamp = new Date(p.row.most_recent_msg_datetime);
                                                        }
                                                        // Removed 10/11/2022 during team call per Bonzana's recommendation
                                                        //else if(p.row.date_created){
                                                        //    latest_timestamp = new Date(p.row.date_created)
                                                        //}
                                                        if (latest_timestamp) {
                                                            const today = new Date();
                                                            const day_from_latest_timestamp = new Date(latest_timestamp);
                                                            // if date is today, show timestamp; otherwise show date
                                                            if (today.setHours(0, 0, 0, 0) === day_from_latest_timestamp.setHours(0, 0, 0, 0)) {
                                                                return latest_timestamp.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
                                                            } else {
                                                                return latest_timestamp.toLocaleDateString('en-US');
                                                            }
                                                        } else {
                                                            return '';
                                                        }
                                                    })()}
                                                </Typography>: <span></span>}
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexWrap: 'nowrap',
                                                        alignItems: 'center',
                                                        justifyContent: 'flex-end',
                                                        marginTop: show1pageScreen ? 4 : 0,
                                                        minHeight: 19,
                                                        width: '100%',
                                                    }}
                                                    key={p.row.id === selectedConversationRow.current?.id ? selectedRowBadgeKey : null}
                                                >
                                                    <Tooltip title={!p.row.subject_user_has_opted_out_of_all_valid_contact_methods ? "Missing contact info" : "User opted out"} arrow placement="top">
                                                        <span>
                                                        {p.row.subject_user_is_not_contactable ? <ErrorOutlineIcon style={{ color: cv('text-muted', '#999999'), height: "18px", width: "18px", flexShrink: 0 }} /> : ""}
                                                        </span>
                                                    </Tooltip>
                                                    {p.row.non_guest_participants_state === 'inactive' && <ArchiveOutlinedIcon style={{ color: cv('text-muted', 'rgba(0, 0, 0, .5)'), height: "17px", width: "17px", minWidth: "16px", marginLeft: 4, flexShrink: 0 }} />}

                                                    {p.row.is_muted_for_user ? <Tooltip title="Notifications muted" arrow placement="top"><span style={{ display: 'inline-flex', flexShrink: 0 }}><NotificationBellOffGray style={{ height: "17px", width: "17px", marginBottom: "1px", marginLeft: "4px" }} /></span></Tooltip> : ""}
                                                    {p.row.subject_user_has_all_notifications_paused ? <Tooltip title={`Notifications${andAiAutoReplies()} paused`} arrow placement="top"><span style={{ display: 'inline-flex', flexShrink: 0 }}><PauseAutoCommunicationsGray style={{ height: "17px", width: "17px", marginBottom: "0.5px", marginLeft: "4px" }} /></span></Tooltip> : ""}
                                                    
                                                    {p.row.non_guest_participants_state !== 'inactive' && (
                                                        <span style={styles.unreadBadge(p.row.unread_message_count)}>{p.row.unread_message_count}</span>
                                                    )}
                                                    {p.row.issues_state === 0 && <Tooltip title="Issues closed" arrow placement="top"><span style={{ ...styles.systemAutoReplyBadge, width: 19, height: 19 }}><IssuesClosedIcon style={{ width: 19, height: 19, display: 'block' }}/></span></Tooltip>}
                                                    {(p.row.issues_state === 2 && !p.row.latest_message_is_system_auto_reply) && <Tooltip title="Issues auto-closed" arrow placement="top"><span style={{ ...styles.systemAutoReplyBadge, width: 21, height: 19 }}><IssuesAutoClosedIcon style={{ width: 21.7, height: 19.8, display: 'block', transform: 'translateY(1.5px)' }}/></span></Tooltip>}
                                                    {p.row.latest_message_is_handoff && <Tooltip title="Handed off — the chatbot stopped and is waiting for your team" arrow placement="top"><span style={{ ...styles.systemAutoReplyBadge, width: 19, height: 19 }}><ChatbotHandoffIcon style={{ width: 19, height: 19, display: 'block' }}/></span></Tooltip>}
                                                    {(p.row.issues_state !== 2 && p.row.latest_message_is_system_auto_reply && !p.row.latest_message_is_handoff) && <Tooltip title="AI auto-replied" arrow placement="top"><span style={{ ...styles.systemAutoReplyBadge, width: 19 }}><SystemAutoReplyIcon style={{ width: 19, height: 17, display: 'block', transform: 'translateY(0.5px)' }}/></span></Tooltip>}
                                                    {(p.row.issues_state === 2 && p.row.latest_message_is_system_auto_reply && !p.row.latest_message_is_handoff) && <Tooltip title="AI auto-replied and auto-closed" arrow placement="top"><span style={{ ...styles.systemAutoReplyBadge, width: 19 }}><SystemAutoReplyAutoClosedIcon style={{ width: 19, height: 17, display: 'block', transform: 'translateY(0.5px)' }}/></span></Tooltip>}

                                                </div>
                                            </div> 
                                        );
                                    },
                                },
                            ]}
                        />
                        </div>
                    </Grid> : null}
            </Grid>}
            {((show1pageScreen && props.selectedScreen !== "ChatScreen") || (props.isEmbeddedInChatList && (!props.chatView || props.chatView !== 'recent_activity'))) ? null :
                <ChatScreen
                    onRef={el => { childChatScreen.current = el; }} // or setState(el)
                    setSelectedScreen={props.setSelectedScreen}
                    chatChannelIdForNavigation={props.selectedChannelID}
                    isGuideGroupChannel={isGuideGroupChannel}
                    show1pageScreen={show1pageScreen}
                    show3PanelChat={props.show3PanelChat}
                    isSmallScreen={isSmallScreen}
                    selectedScreen={props.selectedScreen}
                    paperClass={classes.paper}
                    selectedConversationUniqueName={selectedConversationUniqueNameRef.current}
                    selectedConversationAvatarColor={selectedConversationAvatarColorRef.current}
                    refreshFromBackendFunc={props.activityViewRefreshFromBackendFunc || refreshFromBackendFunc}
                    removeUnreadBadge={props.activityViewRemoveUnreadBadge || removeUnreadBadge}
                    updateIssuesStateBadge={props.activityViewUpdateIssuesStateBadge || updateIssuesStateBadge}
                    updateUnreadBadgeCount={props.activityViewUpdateUnreadBadgeCount || updateUnreadBadgeCount}
                    chatView={props.chatView}
                    setChatView={props.setChatView}
                    selectedConversationsForMessaging={checkboxMode ? Array.from(selectedConversations) : []}
                    checkboxMode={checkboxMode}
                    onClearSelection={() => setSelectedConversations(new Set())}
                    channelTitleForMultipleRecipients={checkboxMode ? headerTitleText : null}
                    travelDateForMultipleRecipients={checkboxMode ? dateSubtext() : null}
                    tourTagsForMultipleRecipients={checkboxMode ? (tourTags || props.selectedChannelTags || []) : null}
                />
            }
        </div>
    );
};

const styles = {
    conversationNameRow: (hasUnreadMessages, props) => ({
        fontWeight: hasUnreadMessages ? '600' : '500',
        fontSize: '1.05rem',
        marginLeft: '15px',
        marginBottom: 0,
        lineHeight: 1.2,
        minWidth:  "190px",
        marginTop: !props.isRecentActivitySupportView && (props.isEmbeddedInChatList || props.isRecentActivityView) ? "-6px" : null,
        // No fixed `width`: let the line auto-fill the cell (like previewText). A fixed
        // width + the marginLeft above would overflow the clip box and push the ellipsis
        // off-screen (hard cut, no "…"). maxWidth keeps it from ever exceeding the cell.
        maxWidth: '100%',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
    }),
    conversationNameText: (hasUnreadMessages, show1pageScreen, props) => ({
        display: "block",
        width: show1pageScreen ? '100%' : undefined,
        // (Was a dead ternary that always resolved to '100%', with one branch holding
        // an invalid '100%)' value the browser dropped.)
        maxWidth: '100%',
        overflow: "hidden",
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        marginRight: 6,
    }),
    previewText: (hasUnreadMessages, show1pageScreen, props) => ({
        fontWeight: hasUnreadMessages ? '500' : 'normal',
        color: hasUnreadMessages ? cv('text', 'black') : cv('text-muted', '#6c757d'),
        fontSize: props.isEmbeddedInChatList || props.isRecentActivityView ? '.8rem' : '.88rem',
        minHeight: '1.35rem',
        marginLeft: '15px',
        display: 'block',
        maxWidth: props.isEmbeddedInChatList && !show1pageScreen 
            ? (!hasUnreadMessages ? '100%' : '100%')
            : (!show1pageScreen ? '100%' : '100%'),
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
    }),
    conversationNameRowInLeftPanel: (hasUnreadMessages, show1pageScreen) => ({
        fontWeight: hasUnreadMessages ? '600' : '500',
        //color: hasUnreadMessages ? 'black' : '#6c757d',
        color: cv('text', 'black'),
        // Use the full cell width. (The previous `calc(100% - 45px)` reserved space
        // for a right-side element that actually lives in the separate date column,
        // so the reservation only made the row too narrow -- enough to push the
        // trailing PAX chip onto its own line.)
        maxWidth: '100%',
        // Render the channel name + tags as a wrapping flex row so the tags flow
        // onto new lines (instead of a single ellipsis-clipped line). Using a flex
        // gap rather than per-chip margins keeps every wrapped line flush-left
        // instead of indenting.
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        columnGap: 4,
        rowGap: 3,
        fontSize: ".8rem",
        marginLeft: "15px",
        paddingRight: 4,
        paddingTop: 0,
        //background: "#EAF4FF",
        borderRadius: 3,
        marginBottom: "5px"
    }),

    timestampText: {
        color: cv('text-muted', '#6c757d'),
        fontSize: '.88rem',
        paddingTop: "3px",
        //minHeight: '1.25rem',
    },
    unreadBadge: (unread_message_count) => ({
        // One digit: a 17px circle. More: a pill that grows with the number (rounded ends,
        // 3px either side) instead of squeezing into the circle.
        borderRadius: '8.5px',
        fontWeight: '700',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        display: unread_message_count > 0 ? 'inline-flex' : 'none',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        minWidth: '17px',
        height: '17px',
        fontSize: '13px',
        fontVariantNumeric: 'tabular-nums',
        color: 'white',
        backgroundColor: cv('accent-fill', '#007AFF'),
        lineHeight: '1',
        boxSizing: 'border-box',
        // Nudges the digits onto the badge's centre. Chrome snaps glyphs to whole device
        // pixels, so this moves in steps, not smoothly: measured 9/29 in headless Chrome,
        // 0.75px centres them at 1x and 1.25x and is within ~0.4px at 1.5x/2x, where
        // 1.75px and up sat ~0.7px low at every scale.
        paddingTop: '0.75px',
        paddingLeft: unread_message_count < 10 ? '0.25px' : '3.25px',
        paddingRight: unread_message_count < 10 ? 0 : '3px',
        marginLeft: 4,
        flexShrink: 0,
    }),
    systemAutoReplyBadge: {
        borderRadius: '50%',
        fontWeight: '600',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        marginLeft: 4,
        width: '17px',
        height: '17px',
        flexShrink: 0,
    },
    header: (show1pageScreen, props) => ({
        color: cv('text', 'black'),
        backgroundColor: cv('surface-muted', '#F5F5F5'),
        marginTop: 0,
        paddingTop: 4,
        paddingBottom: 4,
        paddingLeft: !show1pageScreen ? 12 : 4,
        //alignItems: 'center',
        height: !show1pageScreen ? 109 : 61,
        borderTop: `1px solid ${cv('border', 'rgb(224, 224, 224)')}`,
        borderRight: `1px solid ${cv('border', 'rgb(224, 224, 224)')}`,
        borderLeft: !show1pageScreen ? null : `1px solid ${cv('border', 'rgb(224, 224, 224)')}`,
        borderRadius: !show1pageScreen ? null : "10px 10px 0px 0px",
        display: props?.isEmbeddedInChatList ? "none" : null,
    }),
    topicImg: {
        display: 'inline-block',
        //height: '50px',
        //width: '50px',
        borderRadius: '50%',
        marginRight: 9,
        marginTop: 0,
        height: 40,
        width: 40
        //marginBottom:7
    },
    coverImg: {
        height: '43px',
        width: '43px',
        minWidth: '43px',
        borderRadius: '50%',
        //marginTop: '2px',
    },
    dateStatusLabel: {
        fontSize: '.9rem',
        color: cv('text-muted', '#8A8A8F'),
        fontWeight: '600',
        paddingTop: 12,
        borderTop: `0px solid ${cv('border', 'rgb(224, 224, 224)')}`,
        borderLeft: `1px solid ${cv('border', 'rgb(224, 224, 224)')}`,
        borderRight: `1px solid ${cv('border', 'rgb(224, 224, 224)')}`,
        backgroundColor: cv('surface', 'white'),
        paddingLeft: 12,
    },
    conversationsListHeaderSubtext: (show1pageScreen, isSmallScreen) => ({
        maxWidth: !show1pageScreen || !isSmallScreen ? 'calc(100% - 40px)' : 'calc(100% - 20px)',
        overflowY: "hidden",
        overflowX: !isSmallScreen ? 'hidden' : 'auto',
        whiteSpace: 'nowrap',
        textOverflow: !isSmallScreen ? 'ellipsis' : null,
        color: cv('text-muted', '#6c757d'),
        fontSize: "13px",
        marginTop: !show1pageScreen ? "-10px" : "-3px",
    }),
};

export default ConversationsList;
